/* Astetica Chatbot Widget
 * Usage in theme.liquid (before </body>):
 *   <script src="//astetica.co.uk/cdn/shop/t/1/assets/widget-embed.js?v=106327749636793701721764429911" type="text/javascript"></script>
 *
 * UI-only mode:
 * - If the backend API is not live, the widget still loads.
 * - On send failure it shows:
 *   "The Astetica assistant is not available right now. Please try again later."
 */

(function () {
  if (window.AsteticaChatbotLoaded) return;
  window.AsteticaChatbotLoaded = true;

  const AST_WIDGET_BUILD_ID = "AST-WIDGET-BUILD-20260120-ICONPROOF-A";
  const AST_MOBILE_FULLSCREEN = true;
  const AST_DEBUG_IOS = Boolean(location.search.includes('ast_debug=1') || localStorage.getItem('AST_DEBUG_IOS') === '1');

  // Widget visibility guard - do not show on dedicated chat page
  if (window.__ASTETICA_AI_PAGE__) {
    console.debug('Astetica AI page detected, skipping widget init');
    return;
  }

  const API_ENDPOINT = 'https://asteticachatbot-production.up.railway.app/chat';
  const AST_ANON_ID_KEY = 'ast_anon_id';
  const AST_IDENTITY_TOKEN_KEY = 'ast_identity_token';
  const AST_IDENTITY_FETCHED_AT_KEY = 'ast_identity_token_fetched_at';
  const AST_IDENTITY_ENDPOINT = '/apps/astetica-chat/identity';
  const AST_IDENTITY_TTL_SECONDS = 50 * 60;
  const AST_CHAT_HISTORY_KEY = 'ast_chat_history_widget_v1';
  const AST_CHAT_HISTORY_MAX = 50;
  let astIsRestoringHistory = false;

  function getAstSessionId() {
    const key = 'ast_session_id';
    let sid = null;
    try {
      sid = localStorage.getItem(key);
      if (sid) return sid;
      sid = 'sid_' + ((window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : Date.now() + '_' + Math.random().toString(16).slice(2));
      localStorage.setItem(key, sid);
      return sid;
    } catch (e) {
      return 'sid_' + Date.now() + '_' + Math.random().toString(16).slice(2);
    }
  }

  function generateUuidV4() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    const bytes = new Uint8Array(16);
    if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      window.crypto.getRandomValues(bytes);
    } else {
      for (let i = 0; i < bytes.length; i += 1) {
        bytes[i] = Math.floor(Math.random() * 256);
      }
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, function (b) {
      return b.toString(16).padStart(2, '0');
    }).join('');
    return (
      hex.slice(0, 8) + '-' +
      hex.slice(8, 12) + '-' +
      hex.slice(12, 16) + '-' +
      hex.slice(16, 20) + '-' +
      hex.slice(20)
    );
  }

  function getAnonId() {
    try {
      const existing = localStorage.getItem(AST_ANON_ID_KEY);
      if (existing) return existing;
      const next = generateUuidV4();
      localStorage.setItem(AST_ANON_ID_KEY, next);
      return next;
    } catch (e) {
      return generateUuidV4();
    }
  }

  async function getIdentityToken(options) {
    const forceRefresh = !!(options && options.forceRefresh);
    const nowSec = Math.floor(Date.now() / 1000);

    if (!forceRefresh) {
      try {
        const cachedToken = localStorage.getItem(AST_IDENTITY_TOKEN_KEY);
        const fetchedAtRaw = localStorage.getItem(AST_IDENTITY_FETCHED_AT_KEY);
        const fetchedAt = fetchedAtRaw ? parseInt(fetchedAtRaw, 10) : 0;
        if (cachedToken && fetchedAt > 0 && (nowSec - fetchedAt) < AST_IDENTITY_TTL_SECONDS) {
          return cachedToken;
        }
      } catch (e) {
      }
    }

    try {
      const res = await fetch(AST_IDENTITY_ENDPOINT, {
        method: 'GET',
        cache: 'no-store',
        credentials: 'include'
      });
      if (!res.ok) return null;
      const data = await res.json();
      const token = ((data && (data.identity_token || data.identityToken)) || '').toString().trim();
      if (!token) return null;
      try {
        localStorage.setItem(AST_IDENTITY_TOKEN_KEY, token);
        localStorage.setItem(AST_IDENTITY_FETCHED_AT_KEY, String(nowSec));
      } catch (e) {
      }
      return token;
    } catch (e) {
      return null;
    }
  }

  async function postChatRequest(requestBody, anonId, identityToken) {
    let anonIdHeader = anonId;
    let userIdentityToken = identityToken;
    try {
      if (!anonIdHeader) {
        anonIdHeader = localStorage.getItem('ast_anonymous_id') || localStorage.getItem(AST_ANON_ID_KEY);
      }
      if (!userIdentityToken) {
        userIdentityToken = localStorage.getItem(AST_IDENTITY_TOKEN_KEY);
      }
    } catch (e) {
    }
    const headers = {
      'Content-Type': 'application/json',
      'X-Astetica-Anon-Id': anonIdHeader
    };
    if (userIdentityToken) {
      headers['X-Astetica-Identity-Token'] = userIdentityToken;
    }
    return fetch(API_ENDPOINT, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(requestBody)
    });
  }

  function loadChatHistory() {
    try {
      const raw = localStorage.getItem(AST_CHAT_HISTORY_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter(function (item) {
          return (
            item &&
            (item.role === 'user' || item.role === 'assistant') &&
            typeof item.text === 'string'
          );
        })
        .map(function (item) {
          return {
            role: item.role,
            text: item.text,
            timestamp: item.timestamp
          };
        })
        .slice(-AST_CHAT_HISTORY_MAX);
    } catch (e) {
      return [];
    }
  }

  function saveChatHistory(list) {
    try {
      const safeList = Array.isArray(list) ? list.slice(-AST_CHAT_HISTORY_MAX) : [];
      localStorage.setItem(AST_CHAT_HISTORY_KEY, JSON.stringify(safeList));
    } catch (e) {
    }
  }

  function clearChatHistory() {
    try {
      localStorage.removeItem(AST_CHAT_HISTORY_KEY);
    } catch (e) {
    }
  }

  function pushHistory(role, text) {
    if (role !== 'user' && role !== 'assistant') return;
    if (typeof text !== 'string' || !text) return;
    const history = loadChatHistory();
    history.push({
      role: role,
      text: text,
      timestamp: Date.now()
    });
    saveChatHistory(history);
  }

  function isLocalhost() {
    return (
      location.hostname === 'localhost' ||
      location.hostname === '127.0.0.1'
    );
  }

  // Removed hardcoded greeting - let the model generate greetings naturally from the system prompt
  // The new system prompt instructs the model to vary greetings and be warm/human-like
  const INITIAL_GREETING = null; // No hardcoded greeting - model will generate it
  let astScrollLockActive = false;
  let astScrollLockY = 0;
  let astPrevHtmlOverflow = '';
  let astPrevBodyOverflow = '';
  let astPrevHtmlHeight = '';
  let astPrevBodyHeight = '';
  let astPrevHtmlOverscrollBehavior = '';
  let astPrevBodyOverscrollBehavior = '';
  let astPrevBodyPosition = '';
  let astPrevBodyTop = '';
  let astPrevBodyLeft = '';
  let astPrevBodyRight = '';
  let astPrevBodyWidth = '';
  let astPrevHtmlTouchAction = '';
  let astPrevBodyTouchAction = '';
  let astVVBound = false;
  let astVVHandler = null;
  let astVVDebugRaf = 0;
  let astLastVvH = 0;
  let astLastVvTop = 0;
  let astPostKbRecenterTimer = 0;
  let astDebugLockedScrollY = null;
  let astHeaderBaselineOpenTop = null;
  let astHeaderBaselineFocusTop = null;
  let astIosKbActive = false;
  let astIosKbPrevH = 0;
  let astIosKbPrevBottom = '';
  let astIosKbRaf = 0;
  let astIosFullscreenKbActive = false;
  let astIosFullscreenKbLastTick = 0;
  let astIosFullscreenBaselineH = 0;
  let astForceCloseLockUntil = 0;
  const AST_IOS_KB_HOLD_MS = 250;
  let astKbTransitionStart = 0;
  const AST_IOS_KB_MAX_TRANS_MS = 1200;
let astKbTransitionUntil = 0;
let astKbTransitionResetTimer = 0;
  let astScrollPinHandler = null;
  let astKbScrollSuppressor = null;
  let astFullscreenTouchMoveHandler = null;
  let astIOSInsetUnstable = false;
  let astInsetHistory = [];
  let astInsetScheduleSeq = 0;
  let astInsetTimers = [];
  let astInsetRaf = 0;
  let astLastFocusTapTs = 0;

  // ---------- Inject CSS ----------
  const style = document.createElement('style');
  style.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Josefin+Sans:wght@300;400;500;600&display=swap');

    /* Enforce Josefin Sans font across all chat elements */
    #astetica-chatbot-window,
    #astetica-chatbot-window *:not(svg):not(img):not(#astetica-chatbot-header img) {
      font-family: 'Josefin Sans', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
    }

    /* Root container for launcher and window */
    #astetica-chatbot-root {
      position: fixed;
      inset: auto 24px 24px auto;
      z-index: 2000001; /* Above announcement bar (1000001) and header (999999) */
      font-family: 'Josefin Sans', system-ui, sans-serif;
      background: transparent;
    }

    #astetica-chatbot-root.astetica-chatbot-opened {
      max-width: none !important;
    }

    #astetica-chatbot-root.ast-fullscreen {
      position: fixed !important;
      inset: 0 !important;
      z-index: 2147483647 !important;
      pointer-events: auto !important;
    }

    /* Floating launcher button */
    #astetica-chatbot-launcher {
      width: 72px;
      height: 72px;
      border-radius: 9999px;
      border: 1px solid rgb(0, 0, 0);
      outline: none !important;
      background: radial-gradient(circle at 30% 0%, #FFD6EF 0, #000000 60%, #000000 100%);
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35) !important;
      background-clip: padding-box;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: transform 150ms ease, box-shadow 150ms ease, opacity 150ms ease;
      padding: 0;
      margin: 0;
      overflow: hidden;
    }

    #astetica-chatbot-launcher:focus {
      outline: none;
    }

    #astetica-chatbot-launcher:focus-visible {
      outline: 2px solid rgba(255, 255, 255, 0.55);
      outline-offset: 3px;
    }

    #astetica-chatbot-launcher:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35) !important;
    }

    #astetica-chatbot-launcher img {
      width: 100%;
      height: 100%;
      display: block;
      object-fit: cover;
      border-radius: 9999px;
      border: none !important;
      outline: none !important;
      box-shadow: none !important;
      transform: scale(1.14);
      transform-origin: 50% 50%;
    }

    /* Chat window - floating widget */
    #astetica-chatbot-window {
      position: fixed;
      top: auto;
      left: auto;
      bottom: auto;
      right: auto;
      width: 400px;
      height: 550px;
      max-width: 90vw;
      max-height: 90vh;
      background-color: #FFFFFF;
      border-radius: 20px;
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1);
      display: none;
      flex-direction: column;
      pointer-events: auto;
      border: 1px solid #555555;
      overflow: hidden; /* important so header/messages/input respect rounded corners */
      box-sizing: border-box;
      transition: width 0.2s ease, height 0.2s ease;
      min-width: 280px;
      min-height: 380px;
      overscroll-behavior: contain;
    }

    #astetica-chatbot-window.astetica-chatbot-resizing {
      transition: none !important;
    }

    #astetica-chatbot-window.astetica-chatbot-open {
      display: flex;
    }

    #astetica-chatbot-window.astetica-chatbot-fullscreen {
      position: fixed !important;
      inset: 0 !important;
      left: 0 !important;
      top: 0 !important;
      right: auto !important;
      bottom: auto !important;
      margin: 0 !important;
      width: 100vw !important;
      min-width: 100vw !important;
      height: 100vh !important;
      height: 100dvh !important;
      max-width: none !important;
      max-height: none !important;
      padding-bottom: 0 !important;
      box-sizing: border-box !important;
      display: flex !important;
      flex-direction: column !important;
      overflow: hidden !important;
      overscroll-behavior: none !important;
      transform: none !important;
      border-radius: 0 !important;
      border: none !important;
      z-index: 2147483647 !important;
      transition: none !important;
      min-height: 0 !important;
      --ast-vv-top: 0px;
      --ast-vv-h: 100dvh;
      --ast-kb-inset: 0px;
      --ast-input-h: 0px;
    }

    #astetica-chatbot-window.astetica-chatbot-fullscreen #astetica-chatbot-header {
      flex: 0 0 auto !important;
      /* Do not use sticky here: iOS can treat focus as needing scroll,
         which can push the header out of view during keyboard transitions. */
      position: relative !important;
      top: 0 !important;
      z-index: 5 !important;
      touch-action: auto !important;
      cursor: default !important;
    }

    #astetica-chatbot-window.astetica-chatbot-fullscreen #astetica-chatbot-messages {
      flex: 1 1 auto !important;
      min-height: 0 !important;
      overflow-y: auto !important;
      -webkit-overflow-scrolling: touch !important;
      overscroll-behavior: contain !important;
      /* Reserve space for the keyboard + composer (composer is absolutely positioned). */
      padding-bottom: calc(var(--ast-kb-inset, 0px) + var(--ast-input-h, 0px) + env(safe-area-inset-bottom, 0px)) !important;
    }

    #astetica-chatbot-window.astetica-chatbot-fullscreen #astetica-chatbot-input-area {
      bottom: 0 !important;
      transform: translateY(calc(-1 * var(--ast-kb-inset, 0px))) !important;
      transition: none !important;
      will-change: transform;
      flex: 0 0 auto !important;
      /* Detach composer from layout so only it moves with the keyboard. */
      position: absolute !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      border-radius: 0 !important;
      transform: none !important;
      transform: translateY(calc(-1 * var(--ast-kb-inset, 0px))) !important;
      transition: none !important;
      will-change: transform;
      /* Safe-area is handled via bottom; keep internal padding stable. */
      padding-bottom: 12px !important;
      min-height: 72px !important;
    }

    @media (max-width: 599px) {
      #astetica-chatbot-window {
        width: 95%;
        height: 75vh;
      }

      /* hide the subtitle on mobile */
      #astetica-chatbot-header-subtitle {
        display: none !important;
      }

      /* reduce header padding and center items vertically */
      #astetica-chatbot-header {
        padding: 10px 12px !important;
        display: flex !important;
        align-items: center !important;
      }

      /* ensure header content items are centered */
      #astetica-chatbot-header-content {
        align-items: center !important;
      }

      /* reduce the logo size on mobile */
      #astetica-chatbot-header-content img {
        width: 44px !important;
        height: 44px !important;
        margin-right: 10px !important;
      }

      #astetica-chatbot-header-title {
        white-space: normal !important;
        font-size: 16px;
      }

      #astetica-chatbot-header-content > div {
        padding-left: 4px !important;
        padding-right: 0 !important;
        white-space: normal !important;
        overflow: visible !important;
        text-overflow: clip !important;
      }

      #astetica-chatbot-header {
        padding-right: 16px;
      }

      #astetica-chatbot-header-title {
        transform: translateX(0);
      }

      #astetica-chatbot-header-subtitle {
        transform: translateX(0);
      }

      #astetica-chatbot-header-content {
        gap: 6px;
      }

      #astetica-chatbot-close {
        width: 32px;
        height: 32px;
        background: #ECE9E4;
      }

      #astetica-chatbot-window.astetica-chatbot-fullscreen #astetica-chatbot-bottom-resize-handle {
        display: none !important;
      }
    }

    html.ast-ios #astetica-chatbot-window.astetica-chatbot-open {
      bottom: env(safe-area-inset-bottom, 0px);
    }

    html.ast-ios #astetica-chatbot-window.astetica-chatbot-open.astetica-chatbot-fullscreen {
      bottom: auto !important;
    }

    html.ast-chat-fullscreen,
    html.ast-chat-fullscreen body {
      height: 100% !important;
      overflow: hidden !important;
    }

    /* Chat Header */
    #astetica-chatbot-header {
      background: linear-gradient(
        180deg,
rgba(0, 0, 0, 0.9) 0%,
        #242426 100%
      ) !important;
      flex: 0 0 auto !important;
      color: #ECE9E4;
      padding: 8px 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-family: "Josefin Sans", sans-serif;
      border-bottom: 1px solid rgba(236, 233, 228, 0.3);
      touch-action: none;
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      user-select: none;
    }

    #astetica-chatbot-header-content {
      flex: 1 1 auto;
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 0;
      overflow: visible !important;
    }

    #astetica-chatbot-header-content img {
      width: 56px !important;
      height: 56px !important;
    }

    #astetica-chatbot-header-title {
      font-weight: 300;
      font-size: 18px;
      margin: 2px 0 0 0;
      color: #FFFFFF !important;
      font-family: 'Josefin Sans', sans-serif;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

        #astetica-chatbot-header-content > div {
      flex: 1;
      text-align: center;
      padding-right: 36px;
      min-width: 0;
      overflow: hidden;
    }

    #astetica-chatbot-header button {
      min-width: 0 !important;
      width: auto !important;
    }

    #astetica-chatbot-new-chat {
      flex: 0 0 auto !important;
      width: auto !important;
      min-width: 0 !important;
      display: inline-flex !important;
    }

    #astetica-chatbot-close {
      flex: 0 0 auto !important;
      width: auto !important;
      min-width: 0 !important;
    }

    #astetica-chatbot-new-chat,
    #astetica-chatbot-close {
      flex: 0 0 auto;
      position: relative;
      z-index: 5;
    }

    #astetica-chatbot-header-subtitle {
      font-weight: 300;
      font-size: 12px;
      margin: 0;
      color: #FFFFFF !important;
      opacity: 0.7;
      font-family: 'Josefin Sans', sans-serif;
    }

    #astetica-chatbot-header img {
      color: #FFFFFF !important;
      fill: #FFFFFF !important;
    }

    /* Messages Area */
    #astetica-chatbot-messages {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 20px;
      display: flex;
      flex-direction: column;
      gap: 12px;
      background-color: #1c1c1c !important;
      color: #FFFFFF;
      scrollbar-color: #ECE9E4 #1c1c1c;
      scrollbar-width: thin;
      -webkit-overflow-scrolling: touch;
      touch-action: pan-y;
      overscroll-behavior: contain;
      pointer-events: auto;
      position: relative;
    }

    #astetica-chatbot-messages::-webkit-scrollbar {
      width: 10px;
    }

    #astetica-chatbot-messages::-webkit-scrollbar-track {
      background-color: #1c1c1c !important;
    }

    #astetica-chatbot-messages::-webkit-scrollbar-thumb {
      background-color: #ECE9E4;
      border-radius: 10px;
      box-shadow: inset 0 0 4px rgba(0, 0, 0, 0.25);
    }

    #astetica-chatbot-messages::-webkit-scrollbar-thumb:hover {
      background-color: rgba(236, 233, 228, 0.9);
    }

    #astetica-chatbot-messages::-webkit-scrollbar-button {
      background-color: #ECE9E4;
      color: #000000;
    }

    .astetica-chatbot-message {
      max-width: 80%;
      padding: 12px 16px;
      border-radius: 18px;
      word-wrap: break-word;
      font-size: 13px;
      line-height: 1.6;
      font-family: "Josefin Sans", sans-serif;
    }

    .astetica-chatbot-message--has-image {
      background-color: transparent !important;
      padding: 0;
      border-radius: 0;
      max-width: none;
      gap: 8px;
    }

    .astetica-chatbot-message--has-image .astetica-chatbot-message-text {
      display: inline-block;
      padding: 12px 16px;
      border-radius: 18px;
      font-size: 13px;
      line-height: 1.6;
    }

    .astetica-chatbot-message.astetica-chatbot-user .astetica-chatbot-message-text {
      background-color: #F8D6D9 !important;
      color: #000000 !important;
      border-bottom-right-radius: 4px;
      font-weight: 400;
    }

    .astetica-chatbot-message.astetica-chatbot-assistant .astetica-chatbot-message-text {
      background-color: #ECE9E4 !important;
      color: #000000 !important;
      border-bottom-left-radius: 4px;
      font-weight: 400;
      white-space: pre-line;
    }

    .astetica-chatbot-message--has-image .astetica-chatbot-message-image {
      max-width: 200px;
      max-height: 200px;
      border-radius: 8px;
    }

    .astetica-chatbot-message.astetica-chatbot-user {
      align-self: flex-end;
      background-color: #F8D6D9 !important;
      color: #000000 !important;
      border-bottom-right-radius: 4px;
      font-weight: 400;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .astetica-chatbot-message.astetica-chatbot-user.astetica-chatbot-message--has-image,
    .astetica-chatbot-message.astetica-chatbot-assistant.astetica-chatbot-message--has-image {
      background-color: transparent !important;
    }

    .astetica-chatbot-message-image {
      max-width: 200px;
      max-height: 200px;
      border-radius: 8px;
      object-fit: cover;
      margin-top: 4px;
      cursor: pointer;
    }

    .astetica-chatbot-message.astetica-chatbot-assistant {
      align-self: flex-start;
      background-color: #ECE9E4 !important;
      color: #000000 !important;
      border: none;
      border-bottom-left-radius: 4px;
      font-weight: 400;
    }

    .astetica-chatbot-message.astetica-chatbot-typing {
      align-self: flex-start;
      background-color: #ECE9E4 !important;
      color: #000000 !important;
      font-style: italic;
      border: none;
      border-bottom-left-radius: 4px;
      opacity: 0.8;
    }

    #astetica-chatbot-typing-indicator .astetica-chatbot-typing-dots {
      display: inline-block;
    }

    #astetica-chatbot-typing-indicator .astetica-chatbot-typing-dots span {
      display: inline-block;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background-color: #000000;
      margin: 0 2px;
      animation: astetica-chatbot-typing-bounce 1.4s infinite ease-in-out;
      opacity: 0.6;
    }

    #astetica-chatbot-typing-indicator .astetica-chatbot-typing-dots span:nth-child(1) {
      animation-delay: -0.32s;
    }

    #astetica-chatbot-typing-indicator .astetica-chatbot-typing-dots span:nth-child(2) {
      animation-delay: -0.16s;
    }

    @keyframes astetica-chatbot-typing-bounce {
      0%, 80%, 100% {
        transform: scale(0.8);
        opacity: 0.4;
      }
      40% {
        transform: scale(1);
        opacity: 0.8;
      }
    }

    /* Input Area */
    #astetica-chatbot-input-area {
      padding: 12px 16px 16px 16px;
      display: flex;
      flex-direction: column;
      background-color: #242425 !important;
      border: none;
      border-top: 1px solid #555555;
      border-radius: 0 0 20px 20px !important;
      box-sizing: border-box !important;
      gap: 8px;
      flex: 0 0 auto;
    }

    /* Thumbnail previews container */
    #astetica-chatbot-thumbs {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      width: 100%;
      order: -1;
    }

    .astetica-chatbot-thumb-wrapper {
      position: relative;
      display: inline-block;
      width: 60px;
      height: 60px;
    }

    .astetica-chatbot-thumb {
      width: 60px;
      height: 60px;
      border-radius: 8px;
      object-fit: cover;
      box-shadow: 0 0 4px rgba(0,0,0,0.25);
      display: block;
    }

    /* Input bar row container */
    #astetica-chatbot-input-row {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
    }

    /* Attach button */
    #astetica-chatbot-attach {
      background-color: #ECE9E4;
      color: #000000;
      border: none;
      border-radius: 50%;
      width: 32px;
      height: 32px;
      min-width: 32px;
      padding: 0;
      cursor: pointer;
      transition: all 0.2s ease;
      font-family: "Josefin Sans", sans-serif;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    }

    #astetica-chatbot-attach:hover {
      background-color: rgba(236, 233, 228, 0.95);
      transform: scale(1.05);
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3);
    }

    #astetica-chatbot-attach:active {
      transform: scale(0.95);
    }

    #astetica-chatbot-file-input {
      display: none;
    }

    /* Input pill */
    #astetica-chatbot-input-bar {
      flex: 1;
      min-width: 0;
      background-color: #242425 !important;
      border-radius: 18px !important;
      border: 0.2px solid #555555 !important;
      box-sizing: border-box !important;
      display: flex;
      align-items: center;
      padding: 8px 8px 8px 20px;
      gap: 8px;
      overflow: hidden;
      position: relative;
    }

    #astetica-chatbot-input {
      flex: 1;
      padding: 12px 0;
      border: none !important;
      border-radius: 0;
      font-size: 16px; /* 16px prevents iOS Safari auto-zoom on focus */
      line-height: 20px;
      outline: none;
      font-family: "Josefin Sans", sans-serif;
      background-color: transparent !important;
      color: #F6F1E9 !important;
      transition: all 0.2s ease;
      min-width: 0;
      min-height: 44px;
      max-height: 50vh;
      height: auto;
      resize: none;
      overflow-y: auto;
      white-space: pre-wrap;
      overflow-wrap: anywhere;
      box-sizing: border-box;
      width: 100%;
    }

    #astetica-chatbot-input:focus {
      outline: none !important;
      box-shadow: none !important;
      background: transparent !important;
    }

    #astetica-chatbot-input::placeholder {
      color: #8F8C90 !important;
    }

    #astetica-chatbot-input,
    #astetica-chatbot-input * {
      user-select: text;
      -webkit-user-select: text;
    }
    
    /* === Visible text selection (widget only) === */
    #astetica-chatbot-window ::selection {
    background: rgba(236, 233, 228, 0.65); /* Astetica pink */
    color: #000000;
}

    #astetica-chatbot-window ::-moz-selection {
    background: rgba(236, 233, 228, 0.65);
    color: #000000;
}

    #astetica-chatbot-input::selection,
    #astetica-chatbot-input *::selection {
    background: rgba(236, 233, 228, 0.75);
    color: #000000;
}

    #astetica-chatbot-input::-moz-selection,
    #astetica-chatbot-input *::-moz-selection {
    background: rgba(236, 233, 228, 0.75);
    color: #000000;
}


    #astetica-chatbot-send {
      background-color: #ECE9E4;
      color: #000000;
      border: none;
      border-radius: 50%;
      width: 32px;
      height: 32px;
      min-width: 32px;
      padding: 0;
      cursor: pointer;
      transition: all 0.2s ease;
      font-family: "Josefin Sans", sans-serif;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    #astetica-chatbot-send:hover:not(:disabled) {
      background-color: rgba(236, 233, 228, 0.95);
      transform: scale(1.05);
    }

    #astetica-chatbot-send:active:not(:disabled) {
      transform: scale(0.95);
    }

    #astetica-chatbot-send:disabled {
      background-color: rgba(236, 233, 228, 0.3);
      color: rgba(0, 0, 0, 0.4);
      cursor: not-allowed;
      transform: none;
    }

    /* SVG Icon Styles */
    .astetica-chatbot-icon {
      display: block;
    }

    .astetica-chatbot-icon-plus,
    .astetica-chatbot-icon-arrow {
      width: 60%;
      height: auto;
    }

    .astetica-chatbot-icon-plus line,
    .astetica-chatbot-icon-arrow line,
    .astetica-chatbot-icon-arrow polyline {
      stroke: #000000;
      stroke-width: 2.2;
      stroke-linecap: round;
      stroke-linejoin: round;
      fill: none;
    }

    .astetica-chatbot-icon-arrow line,
    .astetica-chatbot-icon-arrow polyline {
      stroke-width: 2.4;
    }

    /* Close button - unified style */
    .astetica-x-btn {
      background: rgba(236, 233, 228, 0.20);
      border: none;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }

    .astetica-x-btn svg {
      width: 18px;
      height: 18px;
      display: block;
    }

    .astetica-x-btn svg line {
      stroke: #000000;
      stroke-width: 2.6;
      stroke-linecap: round;
    }

    #astetica-chatbot-close {
      width: 32px;
      height: 32px;
      background: #ECE9E4;
      border-radius: 50%;
      color: #000000;
      transition: transform 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
    }

    #astetica-chatbot-close:hover,
    #astetica-chatbot-new-chat:hover,
    #astetica-chatbot-newchat:hover,
    .new-chat-icon-btn:hover {
      background: rgba(236, 233, 228, 0.95);
      transform: scale(1.05);
    }

    #astetica-chatbot-close:active,
    #astetica-chatbot-new-chat:active,
    #astetica-chatbot-newchat:active,
    .new-chat-icon-btn:active {
      transform: scale(0.95);
      box-shadow: none;
    }

    /* Astetica widget close button: white X, transparent circular button */
    #astetica-chatbot-close,
    .astetica-x-btn {
      background: transparent !important;
      box-shadow: none !important;
      color: #ffffff !important;
    }

    #astetica-chatbot-close svg line,
    #astetica-chatbot-close svg path,
    .astetica-x-btn svg line,
    .astetica-x-btn svg path {
      stroke: #ffffff !important;
      fill: none !important;
    }

    #astetica-chatbot-close:hover,
    #astetica-chatbot-close:active,
    #astetica-chatbot-new-chat:hover,
    #astetica-chatbot-new-chat:active,
    #astetica-chatbot-newchat:hover,
    #astetica-chatbot-newchat:active,
    .new-chat-icon-btn:hover,
    .new-chat-icon-btn:active,
    .astetica-x-btn:hover,
    .astetica-x-btn:active {
      background: transparent !important;
      box-shadow: none !important;
    }

    #astetica-chatbot-new-chat:hover,
    #astetica-chatbot-newchat:hover,
    .new-chat-icon-btn:hover,
    #astetica-chatbot-new-chat:active,
    #astetica-chatbot-newchat:active,
    .new-chat-icon-btn:active {
      background: transparent !important;
      background-color: transparent !important;
    }

    #astetica-chatbot-new-chat {
      background-color: transparent;
      background-image: url("https://cdn.shopify.com/s/files/1/0870/1722/6571/files/New_chat_Png.png?v=1770720558") !important;
      background-repeat: no-repeat !important;
      background-position: center !important;
      background-size: 24px 24px !important;
      border: none;
      cursor: pointer;
      padding: 0;
      width: 32px;
      height: 32px;
      display: inline-flex !important;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: opacity 0.2s ease, background-color 0.2s ease;
      margin-right: 8px;
      flex-shrink: 0;
    }

    #astetica-chatbot-new-chat:hover {
      opacity: 0.85;
      background-color: rgba(236, 233, 228, 0.08);
    }

    /* Thumbnail remove button - unified style */
    .astetica-chatbot-thumb-remove {
      position: absolute;
      top: -6px;
      right: -6px;
      width: 22px;
      height: 22px;
      background: rgba(236, 233, 228, 0.35);
      border-radius: 50%;
      color: #000000;
      transition: transform 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
    }

    .astetica-chatbot-thumb-remove:hover {
      background: rgba(236, 233, 228, 0.95);
      transform: scale(1.05);
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3);
    }

    .astetica-chatbot-thumb-remove:active {
      transform: scale(0.95);
    }

    /* Bottom resize handle for mobile widget */
    #astetica-chatbot-bottom-resize-handle {
      width: 100%;
      height: 12px;
      touch-action: none;
      background: transparent;
    }

    @media (min-width: 600px) {
      /* Disable handle on desktop */
      #astetica-chatbot-bottom-resize-handle {
        display: none;
      }
    }

    .new-chat-icon {
      display: none;
    }

    @media (max-width: 749px) {
      #astetica-chatbot-root,
      #astetica-chatbot-header {
        transform: none !important;
        zoom: 1 !important;
      }
    }

    /* --- PATCH: ensure New Chat icon is visible (SVG-first, PNG fallback) --- */
    #astetica-chatbot-new-chat{
      width: 32px !important;
      height: 32px !important;
      padding: 0 !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      flex: 0 0 auto !important;
      flex-shrink: 0 !important;

      /* keep PNG as fallback in case SVG styling fails */
      background-image: url("https://cdn.shopify.com/s/files/1/0870/1722/6571/files/New_chat_Png.png?v=1770720558") !important;
      background-repeat: no-repeat !important;
      background-position: center !important;
      background-size: 24px 24px !important;
    }

    /* override the rule that hides the SVG */
    #astetica-chatbot-new-chat .new-chat-icon{
      display: block !important;
      width: 24px !important;
      height: 24px !important;
      flex: 0 0 auto !important;
    }

    /* If SVG paths are invisible due to fill/stroke, ensure it draws */
    #astetica-chatbot-new-chat .new-chat-icon *{
      stroke: currentColor !important;
      fill: none !important;
    }

    /* (Optional) If the SVG uses fill instead of stroke, swap to:
    #astetica-chatbot-new-chat .new-chat-icon *{ fill: currentColor !important; stroke: none !important; }
    */

    /* --- PATCH: keep PNG icon only, suppress SVG --- */
    #astetica-chatbot-new-chat .new-chat-icon{
      display: none !important;
    }

    #astetica-chatbot-new-chat{
      background-image: url("https://cdn.shopify.com/s/files/1/0870/1722/6571/files/New_chat_Png.png?v=1770720558") !important;
      background-repeat: no-repeat !important;
      background-position: center !important;
      background-size: 24px 24px !important;

      width: 32px !important;
      height: 32px !important;
      padding: 0 !important;

      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;

      flex: 0 0 auto !important;
      flex-shrink: 0 !important;
    }

    @media (min-width: 768px) {
      #astetica-chatbot-header-title {
        transform: translateX(8px) !important;
      }
    }

    @media (max-width: 767px) {
      #astetica-chatbot-header-title {
        transform: translateX(-8px) !important;
      }
    }

    @media (min-width: 768px) {
      #astetica-chatbot-new-chat,
      #astetica-chatbot-newchat,
      .new-chat-icon-btn {
        background: transparent !important; /* prevent dim PNG */
        position: relative !important;
      }

      #astetica-chatbot-new-chat::before,
      #astetica-chatbot-newchat::before,
      .new-chat-icon-btn::before {
        content: "" !important;
        position: absolute !important;
        inset: 0 !important;
        margin: auto !important;
        width: 19px !important;
        height: 19px !important;
        background-color: #ffffff !important;

        -webkit-mask-image: url("https://cdn.shopify.com/s/files/1/0870/1722/6571/files/New_chat_Png.png?v=1770720558") !important;
        -webkit-mask-repeat: no-repeat !important;
        -webkit-mask-position: center !important;
        -webkit-mask-size: contain !important;

        mask-image: url("https://cdn.shopify.com/s/files/1/0870/1722/6571/files/New_chat_Png.png?v=1770720558") !important;
        mask-repeat: no-repeat !important;
        mask-position: center !important;
        mask-size: contain !important;

        pointer-events: none !important;
      }
    }

    @media (min-width: 768px) {
      .astetica-chatbot-thumb-remove {
        width: 32px;
        height: 32px;
        min-width: 32px;
        border-radius: 9999px;
        background-color: #ECE9E4;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .astetica-chatbot-thumb-remove svg {
        width: 14px;
        height: 14px;
        display: block;
      }

      .astetica-chatbot-thumb-remove svg line,
      .astetica-chatbot-thumb-remove svg path {
        stroke: #000000 !important;
        fill: none !important;
      }

      .astetica-chatbot-thumb-remove:hover {
        background-color: rgba(236, 233, 228, 0.95);
        transform: scale(1.05);
        box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3);
      }

      .astetica-chatbot-thumb-remove:active {
        transform: scale(0.95);
      }
    }

    @media (min-width: 768px) {
      .astetica-chatbot-thumb-remove {
        width: 20px !important;
        height: 20px !important;
        min-width: 20px !important;
        min-height: 20px !important;
        border-radius: 9999px !important;
        background: #ECE9E4 !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        padding: 0 !important;
        border: none !important;
      }

      .astetica-chatbot-thumb-remove svg {
        position: relative !important;
        z-index: 1 !important;
      }
    }

    @media (min-width: 768px) {
      .astetica-chatbot-thumb-remove:hover,
      .astetica-chatbot-thumb-remove:active {
        background: #ECE9E4 !important;
        background-color: #ECE9E4 !important;
      }
    }

    @media (max-width: 767px) {
      .astetica-chatbot-thumb-remove {
        width: 20px !important;
        height: 20px !important;
        min-width: 20px !important;
        min-height: 20px !important;
        border-radius: 9999px !important;
        background: #ECE9E4 !important;
        background-color: #ECE9E4 !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        padding: 0 !important;
        border: none !important;
      }

      .astetica-chatbot-thumb-remove:hover,
      .astetica-chatbot-thumb-remove:active {
        background: #ECE9E4 !important;
        background-color: #ECE9E4 !important;
      }

      /* Ensure X is visible on mobile */
      .astetica-chatbot-thumb-remove svg line,
      .astetica-chatbot-thumb-remove svg path {
        stroke: #000000 !important;
        fill: none !important;
      }
    }

  `;
  document.head.appendChild(style);

  // ---------- Inject HTML ----------
  const root = document.createElement('div');
  root.id = 'astetica-chatbot-root';
  root.innerHTML = `
    <button id="astetica-chatbot-launcher" aria-label="Astetica AI Chat">
      <img 
        src="https://cdn.shopify.com/s/files/1/0870/1722/6571/files/Astetica_Ai_widget.png?v=1764198398"
        alt="Astetica AI Chat"
        style="width:100%; height:100%; border-radius:50%; display:block; object-fit:cover;"
      />
    </button>

    <div id="astetica-chatbot-window">
      <div id="astetica-chatbot-header">
        <div id="astetica-chatbot-header-content">
          <img
            src="https://cdn.shopify.com/s/files/1/0870/1722/6571/files/Astetica_Ai_widget_3.png?v=1764199557"
            alt="Astetica Icon"
            style="
              width: 72px;
              height: 72px;
              margin-right: 14px;
              flex-shrink: 0;
              display: block;
              object-fit: cover;
              border-radius: 50%;
            "
          />
          <div>
            <div id="astetica-chatbot-header-title">Astetica Ai assistant</div>
          </div>
        </div>
        <button id="astetica-chatbot-new-chat" aria-label="Clear chat" class="new-chat-icon-btn">
          <svg viewBox="0 0 24 24" class="new-chat-icon" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 20h10a2 2 0 0 0 2-2v-6" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M16 3l5 5-11 11H5v-5L16 3z" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <button id="astetica-chatbot-close" class="astetica-x-btn" aria-label="Close chat">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <line x1="5" y1="5" x2="19" y2="19"
                  stroke="currentColor"
                  stroke-width="2.6"
                  stroke-linecap="round" />
            <line x1="19" y1="5" x2="5" y2="19"
                  stroke="currentColor"
                  stroke-width="2.6"
                  stroke-linecap="round" />
          </svg>
        </button>
      </div>
      <div id="astetica-chatbot-messages"></div>
      <div id="astetica-chatbot-input-area">
        <div id="astetica-chatbot-thumbs"></div>
        <div id="astetica-chatbot-input-row">
          <button id="astetica-chatbot-attach" aria-label="Attach file">
            <svg class="astetica-chatbot-icon astetica-chatbot-icon-plus" viewBox="0 0 24 24" aria-hidden="true">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
          <input 
            type="file" 
            id="astetica-chatbot-file-input" 
            multiple 
            accept="image/*"
            aria-label="File input"
          />
          <div id="astetica-chatbot-input-bar">
            <textarea
              id="astetica-chatbot-input"
              rows="1"
              placeholder="Type your message..."
              aria-label="Message input"
            ></textarea>
            <button id="astetica-chatbot-send" aria-label="Send message">
              <svg class="astetica-chatbot-icon astetica-chatbot-icon-arrow" viewBox="0 0 24 24" aria-hidden="true">
                <line x1="12" y1="19" x2="12" y2="5" />
                <polyline points="6 11, 12 5, 18 11" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div id="astetica-chatbot-bottom-resize-handle" aria-hidden="true"></div>
    </div>
  `;
  document.body.appendChild(root);
  const launcher = document.getElementById('astetica-chatbot-launcher');
  let launcherLastDragAt = 0;
  if (launcher) {
    launcher.addEventListener('click', function (ev) {
      ev.preventDefault();
      ev.stopPropagation();
      if ((Date.now() - launcherLastDragAt) < 500) return;
      openChat();
    });
    launcher.addEventListener('touchend', function (ev) {
      ev.preventDefault();
      ev.stopPropagation();
      if ((Date.now() - launcherLastDragAt) < 500) return;
      openChat();
    }, { passive: false });
  }

  try {
  console.log("[AST_WIDGET] widget-embed.js version: 20260210-NEWCHAT-30PX");

  function applyNewChatIconFix() {
    const btn = document.querySelector("#astetica-chatbot-new-chat") ||
      document.querySelector("#astetica-chatbot-newchat") ||
      document.querySelector(".new-chat-icon-btn");
    if (!btn) return false;
    btn.style.setProperty(
      "background",
      "transparent url('https://cdn.shopify.com/s/files/1/0870/1722/6571/files/New_chat_Png.png?v=1770720558') no-repeat center / 19px 19px",
      "important"
    );
    btn.style.removeProperty("background-blend-mode");
    btn.style.setProperty("width", "30px", "important");
    btn.style.setProperty("height", "30px", "important");
    btn.style.setProperty("minWidth", "30px", "important");
    btn.style.setProperty("minHeight", "30px", "important");
    btn.style.setProperty("padding", "0", "important");
    btn.style.setProperty("border", "0", "important");
    btn.style.setProperty("boxShadow", "none", "important");
    if (window.matchMedia("(max-width: 480px)").matches) {
      btn.style.setProperty("margin-right", "-6px", "important");
    } else {
      btn.style.removeProperty("margin-right");
    }
    // Hide svg/icon child if present
    const svgs = btn.querySelectorAll("svg");
    svgs.forEach(s => (s.style.display = "none"));
    const icon = btn.querySelector(".new-chat-icon");
    if (icon) icon.style.display = "none";
    btn.setAttribute("data-astetica-newchat-fixed", "1");
    return true;
  }

  const applyResult = applyNewChatIconFix();
  if (!applyResult) {
    let attempts = 0;
    const retryId = setInterval(function () {
      attempts += 1;
      if (applyNewChatIconFix() || attempts >= 20) {
        clearInterval(retryId);
      }
    }, 250);
  }

  const newChatObserver = new MutationObserver(function () {
    applyNewChatIconFix();
  });
  newChatObserver.observe(document.body, { subtree: true, childList: true });
  window.addEventListener("resize", applyNewChatIconFix);

  const windowEl = document.getElementById('astetica-chatbot-window');
  const closeBtn = document.getElementById('astetica-chatbot-close');
  const messagesEl = document.getElementById('astetica-chatbot-messages');
  const inputEl = document.getElementById('astetica-chatbot-input');
  const sendBtn = document.getElementById('astetica-chatbot-send');
  const attachBtn = document.getElementById('astetica-chatbot-attach');
  const fileInput = document.getElementById('astetica-chatbot-file-input');
  const thumbsContainer = document.getElementById('astetica-chatbot-thumbs');
  const headerEl = document.getElementById('astetica-chatbot-header');
  const supportsPointer = typeof window !== 'undefined' && !!window.PointerEvent;

  const newChatBtn = document.getElementById('astetica-chatbot-new-chat');

  // Also grab the bottom input area container
  const inputAreaEl = document.getElementById('astetica-chatbot-input-area');

  console.log('[AST_WIDGET]', AST_WIDGET_BUILD_ID, 'loaded', location.href);

  function isIOSDevice() {
    return /iP(hone|od|ad)/.test(navigator.platform) ||
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
  }

  function updateIOSClass() {
    document.documentElement.classList.toggle('ast-ios', !!isIOSDevice());
  }


  // Prevent scroll/touch inside the widget from bubbling up to the page
  function stopTouchPropagation(el) {
    if (!el) return;
    ['touchstart', 'touchmove'].forEach(function (evtName) {
      el.addEventListener(
        evtName,
        function (e) {
          // Let default behaviour (scroll, input) happen,
          // but stop it from reaching the page/body.
          e.stopPropagation();
        },
        { passive: true }
      );
    });
  }

  // Apply to messages area (scroll region) and bottom section
  stopTouchPropagation(messagesEl);
  stopTouchPropagation(inputAreaEl);

  updateIOSClass();

  if (location && location.search && location.search.includes('ast_debug=1')) {
    const closeSvg = closeBtn ? closeBtn.querySelector('svg') : null;
    const closeLines = closeSvg ? closeSvg.querySelectorAll('line') : null;
    if (closeSvg && closeLines && closeLines.length) {
      const svgStyles = getComputedStyle(closeSvg);
      const lineStyles = getComputedStyle(closeLines[0]);
      console.log('[ast_debug][close-icon]', {
        lineCount: closeLines.length,
        svgWidth: svgStyles.width,
        svgHeight: svgStyles.height,
        strokeWidth: lineStyles.strokeWidth
      });
    }
  }

  const bottomResizeHandle = document.getElementById('astetica-chatbot-bottom-resize-handle');

  let isBottomResizingMobile = false;
  let bottomResizeStartY = 0;
  let bottomResizeStartHeight = 0;

  // Store selected images as data URLs
  let widgetSelectedImages = [];

  // ---------- Chat window drag + resize (all 4 edges and corners) ----------

  const MIN_WIDTH = 360;
  const MIN_HEIGHT = 380;
  const EDGE_SIZE = 8;
  const DRAG_THRESHOLD = 5;

  let isWindowDragging = false;
  let isDragCandidate = false;
  let isResizing = false;
  let resizeDir = '';
  let activePointerId = null;

  let startX = 0;
  let startY = 0;
  let startEdges = null;
  let dragStartTarget = null;
  let resizeRafId = 0;
  let pendingResize = null;
  const AST_DEBUG_RESIZE = !!(location && location.search && location.search.includes('ast_debug=1'));
  let lastResizeLog = 0;

  function isInteractiveTarget(target) {
    return !!(
      target &&
      target.closest &&
      target.closest('textarea, input, button, a, [contenteditable="true"], [contenteditable=""], select')
    );
  }

  function loadWindowState() {
    try {
      const raw = localStorage.getItem('astetica-chatbot-window-state');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (
        typeof parsed.left === 'number' &&
        typeof parsed.top === 'number' &&
        typeof parsed.width === 'number' &&
        typeof parsed.height === 'number'
      ) {
        return parsed;
      }
    } catch (e) {
    }
    return null;
  }

  function saveWindowState() {
    try {
      const rect = windowEl.getBoundingClientRect();
      const state = {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height
      };
      localStorage.setItem('astetica-chatbot-window-state', JSON.stringify(state));
    } catch (e) {
    }
  }

  function isMobileViewport() {
    return window.innerWidth <= 599;
  }

  let lastViewportW = window.innerWidth;
  let lastViewportH = window.innerHeight;
  let lastViewportVVH = null;
  let mobileBaseCenterX = null;
  let mobileBaseCenterY = null;

  // Kill switch (set to false to disable without reverting code)
  const AST_IOS_KEYBOARD_FIX = true;
  let iosResizeRaf = 0;
  let iosBaseHeight = 0;
  let iosBaseMaxHeight = 0;
  let astIosKbSnap = null;
  let astPrevRootZIndex = '';

  function isFullscreenWindowActive() {
    return !!(windowEl && windowEl.classList.contains('astetica-chatbot-fullscreen'));
  }

  function isMobileFullscreenActive() {
    return AST_MOBILE_FULLSCREEN && isMobileViewport() && isFullscreenWindowActive();
  }

  function astIsIosFullscreenKbGuarded() {
    if (!isMobileFullscreenActive() || !isIOSDevice()) return false;
    return astIosFullscreenKbActive || ((Date.now() - astIosFullscreenKbLastTick) < AST_IOS_KB_HOLD_MS);
  }

  function astIsIosFullscreenActive() {
    try {
      return isMobileFullscreenActive && isMobileFullscreenActive() && isIOSDevice && isIOSDevice();
    } catch (e) {
      return false;
    }
  }

  function astNoteIosFullscreenKbTick(inset) {
    if (!isMobileFullscreenActive() || !isIOSDevice()) return;
    const now = Date.now();
    if ((inset || 0) > 0) {
      astIosFullscreenKbActive = true;
      astIosFullscreenKbLastTick = now;
      return;
    }
    if (astIosFullscreenKbActive && (now - astIosFullscreenKbLastTick) >= AST_IOS_KB_HOLD_MS) {
      astIosFullscreenKbActive = false;
    }
  }

  function astInKbTransition() {
    const now = Date.now();
    if (!astKbTransitionUntil) return false;
    if (now >= astKbTransitionUntil) {
      astKbTransitionStart = 0;
      return false;
    }
    if (astKbTransitionStart && (now - astKbTransitionStart) > AST_IOS_KB_MAX_TRANS_MS) {
      astKbTransitionUntil = 0;
      astKbTransitionStart = 0;
      return false;
    }
    return true;
  }

  function astSchedulePostKbRecenter(delayMs) {
    if (astPostKbRecenterTimer) clearTimeout(astPostKbRecenterTimer);
    astPostKbRecenterTimer = setTimeout(function () {
      astPostKbRecenterTimer = 0;
      if (!astIsIosFullscreenActive()) return;
      if (astInKbTransition && astInKbTransition()) return;
      try { if (window.scrollY !== 0) window.scrollTo(0, 0); } catch (e) {}
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
    }, delayMs || 700);
  }

  function forceKeyboardUnpinNow() {
    if (!isMobileFullscreenActive() || !isIOSDevice() || !windowEl) return;

    // Stop any pending adaptive inset runs that can keep the composer pinned up.
    astInsetScheduleSeq += 1;
    astInsetTimers.forEach(function (id) { clearTimeout(id); });
    astInsetTimers = [];
    if (astInsetRaf) {
      cancelAnimationFrame(astInsetRaf);
      astInsetRaf = 0;
    }
    if (astPostKbRecenterTimer) {
      clearTimeout(astPostKbRecenterTimer);
      astPostKbRecenterTimer = 0;
    }
    if (astKbTransitionResetTimer) {
      clearTimeout(astKbTransitionResetTimer);
      astKbTransitionResetTimer = 0;
    }

    // End transition/hold states immediately and drop composer inset now.
    astKbTransitionUntil = 0;
    astKbTransitionStart = 0;
    astIosFullscreenKbActive = false;
    astIosFullscreenKbLastTick = 0;
    astIOSInsetUnstable = false;
    astInsetHistory = [];
    const prevTransition = windowEl.style.transition;
    windowEl.style.transition = 'none';
    windowEl.style.setProperty('--ast-kb-inset', '0px');
    // force layout flush so Safari applies immediately
    void windowEl.offsetHeight;
    windowEl.style.transition = prevTransition || '';

    // Verify against delayed iOS viewport updates once the frame settles.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if (!isMobileFullscreenActive() || !isIOSDevice() || !windowEl) return;
        astUpdateVisualViewportVars();
        astUpdateKeyboardInsetVar();
        setTimeout(astUpdateInputHeightVar, 0);
      });
    });
  }

  function astShouldGuardFullscreenGeometry() {
    return !!(windowEl && windowEl.classList.contains('astetica-chatbot-fullscreen') && isIOSDevice() && astInKbTransition());
  }

  function astApplyFullscreenTransitionFreeze() {
    if (!isMobileFullscreenActive() || !windowEl) return;
    windowEl.style.top = '0px';
    windowEl.style.bottom = 'auto';
    windowEl.style.height = '100vh';
    try {
      if (typeof CSS !== 'undefined' && CSS.supports && CSS.supports('height', '100dvh')) {
        windowEl.style.height = '100dvh';
      }
    } catch (e) {}
  }

  function astMarkKbTransition(ms) {
    if (!astKbTransitionStart) astKbTransitionStart = Date.now();
    astKbTransitionUntil = Date.now() + (ms || 450);
    if (!isMobileFullscreenActive() || !windowEl) return;
    astApplyFullscreenTransitionFreeze();
    windowEl.style.transition = 'none';
    if (astKbTransitionResetTimer) {
      clearTimeout(astKbTransitionResetTimer);
    }
    astKbTransitionResetTimer = setTimeout(function () {
      astKbTransitionResetTimer = 0;
      if (!windowEl || !isMobileFullscreenActive()) return;
      if (astInKbTransition()) return;
      windowEl.style.transition = '';
    }, 500);
  }

  function astGetCurrentKbInsetPx() {
    if (!windowEl) return 0;
    const raw = windowEl.style.getPropertyValue('--ast-kb-inset') || '0';
    const n = Math.round(parseFloat(raw) || 0);
    return n > 0 ? n : 0;
  }

  function astClearKbTransitionAndAdaptiveNow() {
    astKbTransitionUntil = 0;
    astKbTransitionStart = 0;
    astIOSInsetUnstable = false;
    astInsetHistory = [];
    astInsetScheduleSeq += 1;
    astInsetTimers.forEach(function (id) { clearTimeout(id); });
    astInsetTimers = [];
    if (astInsetRaf) {
      cancelAnimationFrame(astInsetRaf);
      astInsetRaf = 0;
    }
    if (astKbTransitionResetTimer) {
      clearTimeout(astKbTransitionResetTimer);
      astKbTransitionResetTimer = 0;
    }
    if (windowEl) windowEl.style.transition = '';
  }

  function astDbg(label, data) {
    if (!AST_DEBUG_IOS) return;
    try {
      const time = performance.now();
      console.log('[AST_IOS_DBG]', time.toFixed(1), label, data || {});
    } catch (err) {
      console.log('[AST_IOS_DBG]', 'error logging', err);
    }
  }

  function astIsForceCloseLocked() {
    return (
      isMobileFullscreenActive() &&
      isIOSDevice() &&
      Date.now() < astForceCloseLockUntil
    );
  }

  function astStartForceCloseLock(ms) {
    astForceCloseLockUntil = Date.now() + (ms || 900);
  }

  function astStopForceCloseLock(tag) {
    astForceCloseLockUntil = 0;
  }

  /**
   * iOS FULLSCREEN KEYBOARD STABILISATION NOTE
   *
   * The force-close lock (astForceCloseLockUntil) exists to solve a Safari issue
   * where visualViewport.height lags during keyboard dismissal. Without this,
   * the composer can linger ~1s above the bottom after Send/blur.
   *
   * However, rapid re-focus (<1s after close) previously caused a regression:
   * the lock would still block inset updates, preventing the composer from
   * rising flush above the keyboard.
   *
   * Therefore:
   * - When the keyboard is closing and the composer is NOT focused,
   *   the lock forces inset = 0 and suppresses adaptive scheduling.
   * - If the composer becomes focused again during the lock window,
   *   inset updates are allowed immediately.
   *
   * Do NOT remove the focused override condition:
   *   astIsForceCloseLocked() && !astIsComposerFocused()
   *
   * Removing that condition will reintroduce the rapid reactivation bug.
   */
  function astIsComposerFocused() {
    try { return !!(inputEl && document.activeElement === inputEl); } catch (_) { return false; }
  }

  function astLogStyleWrite(targetName, prop, value, phase) {
    astDbg('style write', {
      target: targetName,
      prop: prop,
      value: value,
      phase: phase || 'before'
    });
  }

  function astHeaderMovedDetector(source) {
    if (!AST_DEBUG_IOS) return;
    if (!isMobileFullscreenActive() || !isIOSDevice()) return;
    const headerEl = document.getElementById('astetica-chatbot-header');
    if (!headerEl) return;
    const headerRect = headerEl.getBoundingClientRect();
    const baseline = astHeaderBaselineFocusTop != null ? astHeaderBaselineFocusTop : astHeaderBaselineOpenTop;
    if (baseline == null) return;
    if (headerRect.top < -1 || Math.abs(headerRect.top - baseline) > 2) {
      astDbg('HEADER_MOVED', {
        source: source,
        baselineTop: baseline,
        currentTop: headerRect.top
      });
      console.warn('[AST][IOS] HEADER MOVED', {
        source: source,
        baselineTop: baseline,
        currentTop: headerRect.top
      });
    }
  }

  function astTraceWrite(label, extra) {
    astDbg('WRITE: ' + label, extra || {});
  }

  function astUpdateKeyboardInsetVar() {
    if (astIsForceCloseLocked() && !astIsComposerFocused()) {
      if (windowEl) {
        const prev = windowEl.style.transition;
        windowEl.style.transition = 'none';
        windowEl.style.setProperty('--ast-kb-inset', '0px');
        void windowEl.offsetHeight;
        windowEl.style.transition = prev || '';
      }
      astClearKbTransitionAndAdaptiveNow();
      return;
    }
    if (!isMobileFullscreenActive()) return;
    if (!isIOSDevice()) return;
    if (!windowEl) return;

    const vv = window.visualViewport;
    if (!vv) return;

    const innerH = Math.round(window.innerHeight || 0);
    const vvH = Math.round(vv.height || 0);
    const vvTop = Math.round(vv.offsetTop || 0);
    // iOS/webviews can report innerHeight shrinking with the keyboard, which collapses
    // (innerH - vvH) towards 0. Use a stable baseline captured on fullscreen open.
    const baselineH = Math.max(
      astIosFullscreenBaselineH || 0,
      (document.documentElement && Math.round(document.documentElement.clientHeight || 0)) || 0,
      innerH
    );
    const keyboardLikelyOpen = vvH > 0 && (baselineH - vvH) > 40;
    const rawInset = keyboardLikelyOpen ? Math.round(baselineH - vvH - vvTop) : 0;
    let inset = rawInset;
    let wasClamped = false;
    if (!Number.isFinite(inset) || inset < 0) {
      inset = 0;
      wasClamped = true;
    }
    if (inset > baselineH) {
      inset = baselineH;
      wasClamped = true;
    }

    astTrackInsetStability(inset, rawInset, wasClamped);
    astNoteIosFullscreenKbTick(inset);

    astTraceWrite('windowEl.--ast-kb-inset', {
      inset: inset,
      innerHeight: innerH,
      vvHeight: vvH,
      vvOffsetTop: vvTop
    });
    windowEl.style.setProperty('--ast-kb-inset', inset + 'px');
    if (inset === 0) {
      if (astIsForceCloseLocked()) {
        astStopForceCloseLock('natural-zero');
      }
      // On keyboard close, do not keep transition/smoothing windows alive.
      astClearKbTransitionAndAdaptiveNow();
    }
    astHeaderMovedDetector('set-kb-inset');
  }

  function astTrackInsetStability(inset, rawInset, wasClamped) {
    if (!isMobileFullscreenActive() || !isIOSDevice()) return;
    const now = Date.now();
    astInsetHistory.push({ t: now, inset: inset });
    if (astInsetHistory.length > 5) {
      astInsetHistory = astInsetHistory.slice(astInsetHistory.length - 5);
    }
    const recent = astInsetHistory.filter(function (entry) {
      return (now - entry.t) <= 400;
    });
    let bigDeltaCount = 0;
    for (let i = 1; i < recent.length; i += 1) {
      if (Math.abs(recent[i].inset - recent[i - 1].inset) > 20) {
        bigDeltaCount += 1;
      }
    }
    if (wasClamped || rawInset < 0 || bigDeltaCount > 2) {
      astIOSInsetUnstable = true;
    }
  }

  function astScheduleKeyboardInsetUpdatePhases() {
    if (!isMobileFullscreenActive() || !isIOSDevice()) return;
    setTimeout(astUpdateVisualViewportVars, 0);
    setTimeout(astUpdateKeyboardInsetVar, 0);
    requestAnimationFrame(function () {
      astScheduleKeyboardInsetUpdateAdaptive();
    });
    setTimeout(astUpdateVisualViewportVars, 80);
    setTimeout(astUpdateKeyboardInsetVar, 80);
    setTimeout(astUpdateVisualViewportVars, 200);
    setTimeout(astUpdateKeyboardInsetVar, 200);
  }

  function astUpdateVisualViewportVars() {
    if (astShouldGuardFullscreenGeometry()) {
      astApplyFullscreenTransitionFreeze();
      return;
    }
    if (!isMobileFullscreenActive()) return;
    if (!isIOSDevice()) return;
    if (!windowEl || !root) return;
    const vv = window.visualViewport;
    if (!vv) return;

    const top = Math.round(vv.offsetTop || 0);
    const h = Math.round(vv.height || 0);
    windowEl.style.setProperty('--ast-vv-top', top + 'px');
    windowEl.style.setProperty('--ast-vv-h', h + 'px');
    root.style.setProperty('--ast-vv-top', top + 'px');
    root.style.setProperty('--ast-vv-h', h + 'px');
  }

  function astUpdateInputHeightVar() {
    if (!windowEl) return;
    const inputArea = document.getElementById('astetica-chatbot-input-area');
    if (!inputArea) return;
    const h = Math.round(inputArea.getBoundingClientRect().height || 0);
    windowEl.style.setProperty('--ast-input-h', h + 'px');
  }

  function astBootstrapKbOpenPin(reason) {
    if (!isMobileFullscreenActive() || !isIOSDevice()) return;
    if (astIsForceCloseLocked() && !(inputEl && document.activeElement === inputEl)) return;
    const run = function () {
      if (!isMobileFullscreenActive() || !isIOSDevice()) return;
      if (astIsForceCloseLocked() && !(inputEl && document.activeElement === inputEl)) return;
      astUpdateVisualViewportVars();
      astUpdateKeyboardInsetVar();
      setTimeout(astUpdateInputHeightVar, 0);
    };
    run();
    requestAnimationFrame(run);
    setTimeout(run, 120);
  }

  function astResetLockedScrollPosition() {
    const docEl = document.documentElement;
    const body = document.body;
    if (docEl && docEl.scrollTop) docEl.scrollTop = 0;
    if (body && body.scrollTop) body.scrollTop = 0;
    try {
      if (window.scrollY !== astScrollLockY) window.scrollTo(0, astScrollLockY);
    } catch (e) {}
  }

  function astScheduleKeyboardInsetUpdateAdaptive() {
    if (astIsForceCloseLocked() && !astIsComposerFocused()) return;
    if (!isMobileFullscreenActive() || !isIOSDevice()) return;

    astInsetTimers.forEach(function (id) { clearTimeout(id); });
    astInsetTimers = [];
    if (astInsetRaf) {
      cancelAnimationFrame(astInsetRaf);
      astInsetRaf = 0;
    }

    const seq = ++astInsetScheduleSeq;
    const run = function () {
      if (seq !== astInsetScheduleSeq) return;
      if (astShouldGuardFullscreenGeometry()) {
        astApplyFullscreenTransitionFreeze();
        astUpdateKeyboardInsetVar();
        setTimeout(astUpdateInputHeightVar, 0);
        if (astGetCurrentKbInsetPx() === 0) return false;
        return;
      }
      astUpdateVisualViewportVars();
      astUpdateKeyboardInsetVar();
      setTimeout(astUpdateInputHeightVar, 0);
      if (astGetCurrentKbInsetPx() === 0) return false;
      return true;
    };

    if (astIOSInsetUnstable) {
      if (run() === false) return;
      astInsetRaf = requestAnimationFrame(run);
      astInsetTimers.push(setTimeout(run, 80));
      astInsetTimers.push(setTimeout(run, 200));
      return;
    }

    if (run() === false) return;
    astInsetTimers.push(setTimeout(run, 80));
  }

  function astSyncFullscreenToVisualViewport() {
    astUpdateVisualViewportVars();
    astUpdateKeyboardInsetVar();
  }

  function bindAstVisualViewport() {
    if (astVVBound) return;
    if (!window.visualViewport) return;
    astVVBound = true;
    astVVHandler = function () {
      if (isMobileFullscreenActive() && isIOSDevice()) {
        if (astIsForceCloseLocked() && !astIsComposerFocused()) {
          return;
        }
        const vv = window.visualViewport;
        const vvH = Math.round((vv && vv.height) || 0);
        const vvTop = Math.round((vv && vv.offsetTop) || 0);
        const vvChanged = (Math.abs(vvH - astLastVvH) > 2) || (Math.abs(vvTop - astLastVvTop) > 2);
        const innerH = Math.round(window.innerHeight || 0);
        const baselineH = Math.max(
          astIosFullscreenBaselineH || 0,
          (document.documentElement && Math.round(document.documentElement.clientHeight || 0)) || 0,
          innerH
        );
        const kbEstimate = Math.max(0, Math.round(baselineH - vvH - vvTop));

        if (vvChanged) {
          if (kbEstimate > 0) {
            astMarkKbTransition(450);
          } else {
            astClearKbTransitionAndAdaptiveNow();
          }
          astLastVvH = vvH;
          astLastVvTop = vvTop;
          astSchedulePostKbRecenter(520);
        }
        if (astInKbTransition()) {
          astApplyFullscreenTransitionFreeze();
          astUpdateKeyboardInsetVar();
          setTimeout(astUpdateInputHeightVar, 0);
          return;
        }
      }
      astDbg('visualViewport event', {
        height: window.visualViewport && window.visualViewport.height,
        offsetTop: window.visualViewport && window.visualViewport.offsetTop
      });
      astUpdateVisualViewportVars();
      astUpdateKeyboardInsetVar();
      setTimeout(astUpdateInputHeightVar, 0);
      if (AST_DEBUG_IOS) {
        if (astVVDebugRaf) return;
        astVVDebugRaf = requestAnimationFrame(function () {
          astVVDebugRaf = 0;
          astDbg('vv-change');
          astHeaderMovedDetector('vv-change');
        });
      }
    };
    window.visualViewport.addEventListener('resize', astVVHandler, { passive: true });
    window.visualViewport.addEventListener('scroll', astVVHandler, { passive: true });
  }

  function unbindAstVisualViewport() {
    if (!astVVBound) return;
    if (!window.visualViewport || !astVVHandler) {
      astVVBound = false;
      astVVHandler = null;
      return;
    }
    window.visualViewport.removeEventListener('resize', astVVHandler);
    window.visualViewport.removeEventListener('scroll', astVVHandler);
    if (astVVDebugRaf) {
      cancelAnimationFrame(astVVDebugRaf);
      astVVDebugRaf = 0;
    }
    astVVBound = false;
    astVVHandler = null;
  }

  function applyMobileFullscreenStyles() {
    if (!windowEl) return;
    astTraceWrite('windowEl.classList.add(astetica-chatbot-fullscreen)');
    astDbg('class toggle', { target: 'windowEl', action: 'add', cls: 'astetica-chatbot-fullscreen' });
    windowEl.classList.add('astetica-chatbot-fullscreen');
    if (root) {
      astPrevRootZIndex = root.style.zIndex || '';
      astTraceWrite('root.style.zIndex=2147483647');
      root.style.zIndex = '2147483647';
    }
  }

  function clearMobileFullscreenStyles() {
    if (!windowEl || !isFullscreenWindowActive()) return;
    astTraceWrite('windowEl clear fullscreen inline styles');
    windowEl.classList.remove('astetica-chatbot-fullscreen');
    windowEl.style.position = '';
    astLogStyleWrite('windowEl', 'left', '', 'before');
    windowEl.style.left = '';
    astLogStyleWrite('windowEl', 'left', windowEl.style.left, 'after');
    astLogStyleWrite('windowEl', 'top', '', 'before');
    windowEl.style.top = '';
    astLogStyleWrite('windowEl', 'top', windowEl.style.top, 'after');
    windowEl.style.right = '';
    windowEl.style.bottom = '';
    windowEl.style.width = '';
    astLogStyleWrite('windowEl', 'height', '', 'before');
    windowEl.style.height = '';
    astLogStyleWrite('windowEl', 'height', windowEl.style.height, 'after');
    windowEl.style.maxHeight = '';
    astLogStyleWrite('windowEl', 'transform', '', 'before');
    windowEl.style.transform = '';
    astLogStyleWrite('windowEl', 'transform', windowEl.style.transform, 'after');
    windowEl.style.borderRadius = '';
    windowEl.style.removeProperty('--ast-vv-top');
    windowEl.style.removeProperty('--ast-vv-h');
    windowEl.style.removeProperty('--ast-kb-inset');
    windowEl.style.background = '';
    if (root) {
      astTraceWrite('root clear fullscreen inline styles');
      astLogStyleWrite('root', 'top', '', 'before');
      root.style.position = '';
      root.style.top = '';
      astLogStyleWrite('root', 'top', root.style.top, 'after');
      astLogStyleWrite('root', 'left', '', 'before');
      root.style.left = '';
      astLogStyleWrite('root', 'left', root.style.left, 'after');
      root.style.width = '';
      astLogStyleWrite('root', 'height', '', 'before');
      root.style.height = '';
      astLogStyleWrite('root', 'height', root.style.height, 'after');
      astLogStyleWrite('root', 'transform', '', 'before');
      astLogStyleWrite('root', 'transform', '', 'after');
      root.style.right = '';
      root.style.bottom = '';
      root.style.removeProperty('--ast-vv-top');
      root.style.removeProperty('--ast-vv-h');
      root.style.zIndex = astPrevRootZIndex;
    }
    astPrevRootZIndex = '';
  }

  function lockBodyScrollForFullscreen() {
    if (astScrollLockActive) return;
    const docEl = document.documentElement;
    const body = document.body;
    if (!docEl || !body) return;

    astScrollLockY = window.scrollY || window.pageYOffset || docEl.scrollTop || 0;

    astPrevHtmlOverflow = docEl.style.overflow || '';
    astPrevBodyOverflow = body.style.overflow || '';
    astPrevHtmlHeight = docEl.style.height || '';
    astPrevBodyHeight = body.style.height || '';
    astPrevHtmlOverscrollBehavior = docEl.style.overscrollBehavior || '';
    astPrevBodyOverscrollBehavior = body.style.overscrollBehavior || '';
    astPrevBodyPosition = body.style.position || '';
    astPrevBodyTop = body.style.top || '';
    astPrevBodyLeft = body.style.left || '';
    astPrevBodyRight = body.style.right || '';
    astPrevBodyWidth = body.style.width || '';
    astPrevHtmlTouchAction = docEl.style.touchAction || '';
    astPrevBodyTouchAction = body.style.touchAction || '';

    astTraceWrite('doc/body lock styles apply', { lockY: astScrollLockY });
    docEl.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    docEl.style.height = '100%';
    body.style.height = '100%';
    docEl.style.overscrollBehavior = (isIOSDevice && isIOSDevice()) ? 'none' : 'contain';
    body.style.overscrollBehavior = 'none';
    docEl.style.touchAction = 'none';
    body.style.touchAction = 'none';
    body.style.position = 'fixed';
    body.style.top = (-astScrollLockY) + 'px';
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    if (astIsIosFullscreenActive()) {
      docEl.style.position = 'fixed';
      docEl.style.top = '0';
      docEl.style.left = '0';
      docEl.style.right = '0';
      docEl.style.width = '100%';
      if (!astScrollPinHandler) {
        astScrollPinHandler = function () {
          if (!astIsIosFullscreenActive()) return;
          const currentY = (window.scrollY || 0) || (window.pageYOffset || 0) || (docEl && docEl.scrollTop) || (body && body.scrollTop) || 0;
          const inTrans = (astInKbTransition && astInKbTransition());
          if (inTrans) {
            body.style.top = (-(astScrollLockY + currentY)) + 'px';
            if (docEl && docEl.scrollTop) docEl.scrollTop = 0;
            if (body && body.scrollTop) body.scrollTop = 0;
            return;
          }

          body.style.top = (-astScrollLockY) + 'px';
          astResetLockedScrollPosition();
        };
        window.addEventListener('scroll', astScrollPinHandler, { passive: true });
      }
      if (!astKbScrollSuppressor) {
        astKbScrollSuppressor = function (e) {
          if (!astIsIosFullscreenActive()) return;
          const inTrans = (astInKbTransition && astInKbTransition());
          if (!inTrans) return;
          const currentY = (window.scrollY || 0) || (window.pageYOffset || 0) || (docEl && docEl.scrollTop) || (body && body.scrollTop) || 0;
          body.style.top = (-(astScrollLockY + currentY)) + 'px';
          if (docEl && docEl.scrollTop) docEl.scrollTop = 0;
          if (body && body.scrollTop) body.scrollTop = 0;
          if (e && e.type === 'wheel' && e.preventDefault) {
            e.preventDefault();
          }
          if (e && e.stopImmediatePropagation) {
            e.stopImmediatePropagation();
          }
        };
        window.addEventListener('scroll', astKbScrollSuppressor, true);
        window.addEventListener('wheel', astKbScrollSuppressor, { passive: false, capture: true });
      }
      astResetLockedScrollPosition();
    }
    if (!astFullscreenTouchMoveHandler) {
      astFullscreenTouchMoveHandler = function (e) {
        if (!astIsIosFullscreenActive()) return;
        const t = e && e.target;
        if (t && t.closest && t.closest('#astetica-chatbot-messages')) return;
        e.preventDefault();
        astResetLockedScrollPosition();
      };
    }
    document.addEventListener('touchmove', astFullscreenTouchMoveHandler, { passive: false, capture: true });

    astScrollLockActive = true;
    astDebugLockedScrollY = astScrollLockY;
    astDbg('scrollLock', { type: 'lock', scrollY: window.scrollY });
  }

  function unlockBodyScrollForFullscreen() {
    if (!astScrollLockActive) return;
    const docEl = document.documentElement;
    const body = document.body;
    if (!docEl || !body) {
      astScrollLockActive = false;
      return;
    }

    astTraceWrite('doc/body lock styles restore', { restoreY: astScrollLockY });
    docEl.style.overflow = astPrevHtmlOverflow;
    body.style.overflow = astPrevBodyOverflow;
    docEl.style.height = astPrevHtmlHeight;
    body.style.height = astPrevBodyHeight;
    docEl.style.overscrollBehavior = astPrevHtmlOverscrollBehavior;
    body.style.overscrollBehavior = astPrevBodyOverscrollBehavior;
    docEl.style.touchAction = astPrevHtmlTouchAction;
    body.style.touchAction = astPrevBodyTouchAction;
    body.style.position = astPrevBodyPosition;
    body.style.top = astPrevBodyTop;
    body.style.left = astPrevBodyLeft;
    body.style.right = astPrevBodyRight;
    body.style.width = astPrevBodyWidth;
    docEl.style.position = '';
    docEl.style.top = '';
    docEl.style.left = '';
    docEl.style.right = '';
    docEl.style.width = '';
    body.style.left = '';
    body.style.right = '';
    if (astFullscreenTouchMoveHandler) {
      document.removeEventListener('touchmove', astFullscreenTouchMoveHandler, true);
    }

    window.scrollTo(0, astScrollLockY);
    astScrollLockActive = false;
    if (astScrollPinHandler) {
      window.removeEventListener('scroll', astScrollPinHandler);
      astScrollPinHandler = null;
    }
    if (astKbScrollSuppressor) {
      window.removeEventListener('scroll', astKbScrollSuppressor, true);
      window.removeEventListener('wheel', astKbScrollSuppressor, true);
      astKbScrollSuppressor = null;
    }
    if (astPostKbRecenterTimer) {
      clearTimeout(astPostKbRecenterTimer);
      astPostKbRecenterTimer = 0;
    }
    astDebugLockedScrollY = null;
    astDbg('scrollLock', { type: 'unlock', scrollY: window.scrollY });
  }

  function getViewportHeightPx() {
    return Math.round(
      window.innerHeight ||
      document.documentElement.clientHeight ||
      0
    );
  }

  function getVisualViewportCenterPx() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const offsetLeft = 0;
    const offsetTop = 0;
    return {
      x: Math.round(offsetLeft + (w / 2)),
      y: Math.round(offsetTop + (h / 2))
    };
  }

  function applyMobileBaseCenterPosition() {
    if (!windowEl) return;
    if (isMobileFullscreenActive()) return;
    if (astShouldGuardFullscreenGeometry()) return;
    if (astIsIosFullscreenKbGuarded()) return;
    if (windowEl.dataset.dragged === '1') return;
    if (!isMobileViewport()) return;
    if (mobileBaseCenterX == null || mobileBaseCenterY == null) return;
    windowEl.style.left = mobileBaseCenterX + 'px';
    windowEl.style.top = mobileBaseCenterY + 'px';
    windowEl.style.right = 'auto';
    windowEl.style.bottom = 'auto';
    windowEl.style.transform = 'translate(-50%, -50%)';
  }

  function isKeyboardOpen(vv) {
    if (!vv || !vv.height) return false;
    return (window.innerHeight - vv.height) > 120;
  }

  function isKeyboardOpenIOS() {
    return false;
  }

  function shouldBlockDragIOS() {
    return isIOSDevice() && (isKeyboardOpenIOS() || document.activeElement === inputEl);
  }

  function isHeaderInteractiveTarget(target) {
    return !!(
      target &&
      target.closest &&
      target.closest('button,a,input,textarea,select,[role="button"]')
    );
  }

  function astSnapshot(el) {
    if (!el) return null;
    return {
      position: el.style.position || '',
      left: el.style.left || '',
      right: el.style.right || '',
      top: el.style.top || '',
      bottom: el.style.bottom || '',
      width: el.style.width || '',
      height: el.style.height || '',
      maxHeight: el.style.maxHeight || '',
      transform: el.style.transform || '',
      transition: el.style.transition || ''
    };
  }

  function astRestore(el, s) {
    if (!s) return;
    el.style.position = s.position;
    el.style.left = s.left;
    el.style.right = s.right;
    el.style.top = s.top;
    el.style.bottom = s.bottom;
    el.style.width = s.width;
    el.style.height = s.height;
    el.style.maxHeight = s.maxHeight;
    el.style.transform = s.transform;
    el.style.transition = s.transition;
  }

  function astIsIosMobile() {
    return AST_IOS_KEYBOARD_FIX && isIOSDevice && isIOSDevice() && isMobileViewport && isMobileViewport();
  }

  function astKeyboardShouldPin() {
    return false;
  }

  function astLayoutHeight() {
    return (document.documentElement && document.documentElement.clientHeight) ? document.documentElement.clientHeight : window.innerHeight;
  }

  function astKeyboardOffsetPx() {
    return 0;
  }

  function astApplyPinnedToKeyboard() {
    return;
  }

  function astExitPinnedKeyboard() {
    astIosKbSnap = null;
    astIosKbPrevH = 0;
    astIosKbPrevBottom = '';
  }

  function astUpdateIosKeyboardPin() {
    astIosKbActive = false;
  }

  function saveDraggedPosition() {
    if (!windowEl) return;
    const rect = windowEl.getBoundingClientRect();
    windowEl.dataset.dragged = '1';
    windowEl.dataset.dragLeft = String(rect.left);
    windowEl.dataset.dragTop = String(rect.top);
  }

  function restoreDraggedPosition() {
    if (!windowEl) return;
    if (windowEl.dataset.dragged !== '1') return;
    const left = parseFloat(windowEl.dataset.dragLeft);
    const top = parseFloat(windowEl.dataset.dragTop);
    if (!Number.isFinite(left) || !Number.isFinite(top)) return;
    windowEl.style.left = left + 'px';
    windowEl.style.top = top + 'px';
    windowEl.style.right = 'auto';
    windowEl.style.bottom = 'auto';
    windowEl.style.transform = '';
  }

  function applyIOSBaseSize() {
    if (isMobileFullscreenActive()) return;
    if (astShouldGuardFullscreenGeometry()) return;
    if (astIsIosFullscreenKbGuarded()) return;
    if (!isMobileViewport() || !isIOSDevice()) return;
    const viewportHeight = getViewportHeightPx();
    if (!viewportHeight) return;
    const targetHeight = Math.max(MIN_HEIGHT, Math.round(viewportHeight * 0.75));
    iosBaseHeight = targetHeight;
    iosBaseMaxHeight = 0;
    windowEl.style.height = targetHeight + 'px';
  }

  function updateIOSKeyboardLayout() {
    if (isMobileFullscreenActive()) return;
    if (astShouldGuardFullscreenGeometry()) return;
    if (astIsIosFullscreenKbGuarded()) return;
    if (AST_IOS_KEYBOARD_FIX) {
      astUpdateIosKeyboardPin();
      if (astIosKbActive) {
        return;
      }
    }

    if (!isMobileViewport() || !isIOSDevice()) return;
    if (!windowEl.classList.contains('astetica-chatbot-open')) return;

    if (iosBaseHeight) {
      windowEl.style.height = iosBaseHeight + 'px';
    }
  }

  function scheduleIOSViewportSizing() {
    if (isMobileFullscreenActive()) return;
    if (astShouldGuardFullscreenGeometry()) return;
    if (astIsIosFullscreenKbGuarded()) return;
    if (iosResizeRaf) {
      window.cancelAnimationFrame(iosResizeRaf);
    }
    iosResizeRaf = window.requestAnimationFrame(function () {
      iosResizeRaf = 0;
      updateIOSKeyboardLayout();
    });
  }

  function clampRectToViewport(left, top, width, height) {
    const margin = 25;
    const maxLeft = window.innerWidth - margin;
    const maxTop = window.innerHeight - margin;
    const minLeft = -(width - margin);
    const minTop = -(height - margin);

    return {
      left: Math.max(minLeft, Math.min(left, maxLeft)),
      top: Math.max(minTop, Math.min(top, maxTop))
    };
  }

  function getResizeDir(clientX, clientY) {
    const rect = windowEl.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    const onLeft = x >= 0 && x <= EDGE_SIZE;
    const onRight = x <= rect.width && x >= rect.width - EDGE_SIZE;
    const onTop = y >= 0 && y <= EDGE_SIZE;
    const onBottom = y <= rect.height && y >= rect.height - EDGE_SIZE;

    let dir = '';
    if (onTop) dir += 'n';
    if (onBottom) dir += 's';
    if (onLeft) dir += 'w';
    if (onRight) dir += 'e';

    return dir;
  }

  function updateWindowCursor(clientX, clientY, target) {
    const dir = getResizeDir(clientX, clientY);
    if (isFullscreenWindowActive()) {
      windowEl.style.cursor = '';
      return;
    }
    let cursor = '';

    if (dir === 'n' || dir === 's') cursor = 'ns-resize';
    else if (dir === 'e' || dir === 'w') cursor = 'ew-resize';
    else if (dir === 'ne' || dir === 'sw') cursor = 'nesw-resize';
    else if (dir === 'nw' || dir === 'se') cursor = 'nwse-resize';
    else if (target && target.closest && target.closest('#astetica-chatbot-header')) cursor = 'move';

    windowEl.style.cursor = cursor;
  }

  windowEl.addEventListener('mousemove', function (e) {
    if (!windowEl.classList.contains('astetica-chatbot-open')) return;
    if (isWindowDragging || isResizing) return;
    updateWindowCursor(e.clientX, e.clientY, e.target);
  });

  windowEl.addEventListener('mouseleave', function () {
    if (!isWindowDragging && !isResizing) {
      windowEl.style.cursor = '';
    }
  });

  windowEl.addEventListener('mousedown', function (e) {
    if (typeof window !== 'undefined' && 'ontouchstart' in window && isMobileViewport()) {
      return;
    }

    if (e.button !== 0) return;
    if (!windowEl.classList.contains('astetica-chatbot-open')) return;
    if (isFullscreenWindowActive()) return;
    if (astIosKbActive) return;

    const isHeaderTarget = e.target && e.target.closest && e.target.closest('#astetica-chatbot-header');
    if (isHeaderTarget && shouldBlockDragIOS()) {
      return;
    }

    resizeDir = getResizeDir(e.clientX, e.clientY);
    const wantsResize = resizeDir !== '';

    // Never start drag/resize from within the composer or messages subtree,
    // unless the pointer is within the resize edge area.
    if (!wantsResize && e.target && e.target.closest) {
      const inComposer =
        e.target.closest('#astetica-chatbot-input-area') ||
        e.target.closest('#astetica-chatbot-input-bar') ||
        e.target.closest('#astetica-chatbot-input');
      if (inComposer) return;
      if (e.target.closest('#astetica-chatbot-messages')) return;
    }

    if (!wantsResize && isInteractiveTarget(e.target)) {
      return;
    }

    dragStartTarget = e.target;

    if (e.target && e.target.closest && e.target.closest('#astetica-chatbot-close')) {
      return;
    }

    // Do not start drag when clicking the new chat button
    if (e.target && e.target.closest && e.target.closest('#astetica-chatbot-new-chat')) {
      return;
    }

    const currentRect = windowEl.getBoundingClientRect();
    windowEl.style.transform = '';
    windowEl.style.left = currentRect.left + 'px';
    windowEl.style.top = currentRect.top + 'px';
    windowEl.style.right = 'auto';
    windowEl.style.bottom = 'auto';

    const rect = windowEl.getBoundingClientRect();
    startEdges = {
      left: rect.left,
      right: rect.right,
      top: rect.top,
      bottom: rect.bottom
    };
    startX = e.clientX;
    startY = e.clientY;

    isResizing = resizeDir !== '';
    const isHeaderDrag = !!(e.target && e.target.closest && e.target.closest('#astetica-chatbot-header'));
    if (!isResizing && !isHeaderDrag) {
      return;
    }
    if (isResizing) {
      windowEl.classList.add('astetica-chatbot-resizing');
      const styleLeft = parseFloat(windowEl.style.left);
      const styleTop = parseFloat(windowEl.style.top);
      const styleWidth = parseFloat(windowEl.style.width);
      const styleHeight = parseFloat(windowEl.style.height);
      const startLeft = Number.isFinite(styleLeft) ? styleLeft : windowEl.offsetLeft;
      const startTop = Number.isFinite(styleTop) ? styleTop : windowEl.offsetTop;
      const startW = Number.isFinite(styleWidth) ? styleWidth : windowEl.offsetWidth;
      const startH = Number.isFinite(styleHeight) ? styleHeight : windowEl.offsetHeight;
      startEdges = {
        left: startLeft,
        right: startLeft + startW,
        top: startTop,
        bottom: startTop + startH
      };
    }
    isWindowDragging = false;
    isDragCandidate = !isResizing;

    if (isResizing) {
      document.body.style.userSelect = 'none';
      e.preventDefault();
    }
  });

  document.addEventListener('mousemove', function (e) {
    if (!windowEl.classList.contains('astetica-chatbot-open')) return;
    if (isFullscreenWindowActive()) {
      resetDragState();
      return;
    }
    if (isIOSDevice() && isKeyboardOpenIOS()) {
      resetDragState();
      return;
    }
    if (!isWindowDragging && !isResizing && !isDragCandidate) return;
    if (!startEdges) return;

    if (
      isDragCandidate &&
      (!dragStartTarget || !dragStartTarget.closest || !dragStartTarget.closest('#astetica-chatbot-header'))
    ) {
      isDragCandidate = false;
      return;
    }

    const dx = e.clientX - startX;
    const dy = e.clientY - startY;

    if (!isWindowDragging && isDragCandidate) {
      if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) {
        return;
      }
      isDragCandidate = false;
      isWindowDragging = true;
      document.body.style.userSelect = 'none';
    }

    if (isWindowDragging) {
      e.preventDefault();
      const width = startEdges.right - startEdges.left;
      const height = startEdges.bottom - startEdges.top;

      let newLeft = startEdges.left + dx;
      let newTop = startEdges.top + dy;

      const clamped = clampRectToViewport(newLeft, newTop, width, height);

      windowEl.style.left = clamped.left + 'px';
      windowEl.style.top = clamped.top + 'px';
      windowEl.style.right = 'auto';
      windowEl.style.bottom = 'auto';
      return;
    }

    if (isResizing) {
      e.preventDefault();
      // Anchored resize: keep the opposite edge/corner fixed to avoid jitter.
      const startLeft = startEdges.left;
      const startTop = startEdges.top;
      const startRight = startEdges.right;
      const startBottom = startEdges.bottom;
      const maxWidth = Math.max(MIN_WIDTH, Math.floor(document.documentElement.clientWidth * 0.9));
      const maxHeight = Math.max(MIN_HEIGHT, Math.floor(document.documentElement.clientHeight * 0.9));

      let newLeft = startLeft;
      let newRight = startRight;
      let newTop = startTop;
      let newBottom = startBottom;

      if (resizeDir.includes('w')) {
        let nextLeft = startLeft + dx;
        let nextWidth = startRight - nextLeft;
        if (nextWidth < MIN_WIDTH) {
          nextLeft = startRight - MIN_WIDTH;
        } else if (nextWidth > maxWidth) {
          nextLeft = startRight - maxWidth;
        }
        newLeft = nextLeft;
      }

      if (resizeDir.includes('e')) {
        let nextRight = startRight + dx;
        let nextWidth = nextRight - startLeft;
        if (nextWidth < MIN_WIDTH) {
          nextRight = startLeft + MIN_WIDTH;
        } else if (nextWidth > maxWidth) {
          nextRight = startLeft + maxWidth;
        }
        newRight = nextRight;
      }

      if (resizeDir.includes('n')) {
        let nextTop = startTop + dy;
        let nextHeight = startBottom - nextTop;
        if (nextHeight < MIN_HEIGHT) {
          nextTop = startBottom - MIN_HEIGHT;
        } else if (nextHeight > maxHeight) {
          nextTop = startBottom - maxHeight;
        }
        newTop = nextTop;
      }

      if (resizeDir.includes('s')) {
        let nextBottom = startBottom + dy;
        let nextHeight = nextBottom - startTop;
        if (nextHeight < MIN_HEIGHT) {
          nextBottom = startTop + MIN_HEIGHT;
        } else if (nextHeight > maxHeight) {
          nextBottom = startTop + maxHeight;
        }
        newBottom = nextBottom;
      }

      const width = newRight - newLeft;
      const height = newBottom - newTop;

      if (AST_DEBUG_RESIZE) {
        const now = Date.now();
        if (now - lastResizeLog > 120) {
          lastResizeLog = now;
          console.log('[ast_debug][widget][resize]', {
            resizeDir,
            startEdges,
            newLeft,
            newRight,
            newTop,
            newBottom,
            width,
            height,
            source: 'style/offset'
          });
        }
      }

      pendingResize = {
        left: newLeft,
        top: newTop,
        width,
        height
      };

      if (!resizeRafId) {
        resizeRafId = requestAnimationFrame(function () {
          resizeRafId = 0;
          if (!pendingResize) return;
          if (!windowEl || !windowEl.isConnected) {
            pendingResize = null;
            return;
          }
          const next = pendingResize;
          pendingResize = null;
          windowEl.style.left = next.left + 'px';
          windowEl.style.top = next.top + 'px';
          windowEl.style.width = next.width + 'px';
          windowEl.style.height = next.height + 'px';
          windowEl.style.right = 'auto';
          windowEl.style.bottom = 'auto';
        });
      }
    }
  });

  document.addEventListener('mouseup', function () {
    if (isWindowDragging || isResizing || isDragCandidate) {
      isWindowDragging = false;
      isResizing = false;
      isDragCandidate = false;
      resizeDir = '';
      windowEl.classList.remove('astetica-chatbot-resizing');
      if (resizeRafId) {
        cancelAnimationFrame(resizeRafId);
        resizeRafId = 0;
      }
      if (pendingResize) {
        windowEl.style.left = pendingResize.left + 'px';
        windowEl.style.top = pendingResize.top + 'px';
        windowEl.style.width = pendingResize.width + 'px';
        windowEl.style.height = pendingResize.height + 'px';
        windowEl.style.right = 'auto';
        windowEl.style.bottom = 'auto';
        pendingResize = null;
      }
      startEdges = null;
      dragStartTarget = null;
      document.body.style.userSelect = '';
      windowEl.style.cursor = '';
      saveWindowState();
    }
  });

  function resetDragState() {
    if (isWindowDragging || isResizing || isDragCandidate) {
      isWindowDragging = false;
      isResizing = false;
      isDragCandidate = false;
      resizeDir = '';
      windowEl.classList.remove('astetica-chatbot-resizing');
      if (resizeRafId) {
        cancelAnimationFrame(resizeRafId);
        resizeRafId = 0;
      }
      pendingResize = null;
      startEdges = null;
      dragStartTarget = null;
      document.body.style.userSelect = '';
      windowEl.style.cursor = '';
    }
  }

  window.addEventListener('blur', resetDragState);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') resetDragState();
  });

  function beginMobileDrag(clientX, clientY) {
    if (isFullscreenWindowActive()) return;
    const currentRect = windowEl.getBoundingClientRect();
    windowEl.style.transform = '';
    windowEl.style.left = currentRect.left + 'px';
    windowEl.style.top = currentRect.top + 'px';
    windowEl.style.right = 'auto';
    windowEl.style.bottom = 'auto';

    const rect = windowEl.getBoundingClientRect();
    startEdges = {
      left: rect.left,
      right: rect.right,
      top: rect.top,
      bottom: rect.bottom
    };
    startX = clientX;
    startY = clientY;

    isResizing = false;
    resizeDir = '';
    isWindowDragging = true;
    document.body.style.userSelect = 'none';
  }

  // --- Touch drag handlers for chat window (mobile) ---
  function handleMobileHeaderTouchStart(e) {
    if (!windowEl.classList.contains('astetica-chatbot-open')) return;
    if (!isMobileViewport()) return;
    if (isFullscreenWindowActive()) return;
    if (!e.touches || e.touches.length === 0) return;
    if (astIosKbActive) return;

    const target = e.target;
    if (supportsPointer) return;
    if (isHeaderInteractiveTarget(target)) return;

    // Allow scroll inside the messages area – do not start drag there

    // Allow normal interaction with input area, buttons, thumbnails – no drag

    // Do not start drag when touching the close button

    const touch = e.touches[0];
    e.preventDefault();
    e.stopPropagation();

    if (isIOSDevice()) {
      const ae = document.activeElement;
      if (ae && windowEl.contains(ae) && ae.blur) {
        ae.blur();
      }
    }

    beginMobileDrag(touch.clientX, touch.clientY);
  }

  function handleHeaderPointerDown(e) {
    if (!windowEl.classList.contains('astetica-chatbot-open')) return;
    if (!isMobileViewport()) return;
    if (isFullscreenWindowActive()) return;
    if (e.isPrimary === false) return;
    if (e.button != null && e.button !== 0) return;
    if (astIosKbActive) return;
    if (isHeaderInteractiveTarget(e.target)) return;

    e.preventDefault();
    e.stopPropagation();

    if (isIOSDevice()) {
      const ae = document.activeElement;
      if (ae && windowEl.contains(ae) && ae.blur) {
        ae.blur();
      }
    }

    activePointerId = e.pointerId;
    try {
      if (headerEl && headerEl.setPointerCapture) {
        headerEl.setPointerCapture(e.pointerId);
      }
    } catch (err) {
    }

    beginMobileDrag(e.clientX, e.clientY);
  }

  function handleHeaderPointerMove(e) {
    if (!windowEl.classList.contains('astetica-chatbot-open')) return;
    if (!isMobileViewport()) return;
    if (isFullscreenWindowActive()) {
      resetDragState();
      return;
    }
    if (!isWindowDragging || !startEdges) return;
    if (activePointerId != null && e.pointerId !== activePointerId) return;

    const dx = e.clientX - startX;
    const dy = e.clientY - startY;

    const width = startEdges.right - startEdges.left;
    const height = startEdges.bottom - startEdges.top;

    let newLeft = startEdges.left + dx;
    let newTop = startEdges.top + dy;

    const clamped = clampRectToViewport(newLeft, newTop, width, height);

    windowEl.style.left = clamped.left + 'px';
    windowEl.style.top = clamped.top + 'px';
    windowEl.style.right = 'auto';
    windowEl.style.bottom = 'auto';

    e.preventDefault();
  }

  function handleHeaderPointerUp(e) {
    if (activePointerId != null && e.pointerId !== activePointerId) return;
    activePointerId = null;
    if (!isWindowDragging && !isResizing) return;

    isWindowDragging = false;
    isResizing = false;
    resizeDir = '';
    startEdges = null;
    document.body.style.userSelect = '';
    windowEl.style.cursor = '';
    saveWindowState();

    try {
      if (headerEl && headerEl.releasePointerCapture) {
        headerEl.releasePointerCapture(e.pointerId);
      }
    } catch (err) {
    }
  }

  if (headerEl) {
    if (supportsPointer) {
      headerEl.addEventListener('pointerdown', handleHeaderPointerDown, { passive: false, capture: true });
      document.addEventListener('pointermove', handleHeaderPointerMove, { passive: false });
      document.addEventListener('pointerup', handleHeaderPointerUp, { passive: false });
      document.addEventListener('pointercancel', handleHeaderPointerUp, { passive: false });
    } else {
      headerEl.addEventListener('touchstart', handleMobileHeaderTouchStart, { passive: false, capture: true });
    }
  }

  document.addEventListener('touchmove', function (e) {
    if (!windowEl.classList.contains('astetica-chatbot-open')) return;
    if (!isMobileViewport()) return;
    if (isFullscreenWindowActive()) {
      resetDragState();
      return;
    }
    if (supportsPointer) return;
    if (isIOSDevice() && isKeyboardOpenIOS()) {
      resetDragState();
      return;
    }
    if (!isWindowDragging && !isResizing) return;
    if (!startEdges) return;
    if (!e.touches || e.touches.length === 0) return;

    const touch = e.touches[0];
    const dx = touch.clientX - startX;
    const dy = touch.clientY - startY;

    if (isWindowDragging) {
      // Keep the same size, only move position
      const width = startEdges.right - startEdges.left;
      const height = startEdges.bottom - startEdges.top;

      let newLeft = startEdges.left + dx;
      let newTop = startEdges.top + dy;

      const clamped = clampRectToViewport(newLeft, newTop, width, height);

      windowEl.style.left = clamped.left + 'px';
      windowEl.style.top = clamped.top + 'px';
      windowEl.style.right = 'auto';
      windowEl.style.bottom = 'auto';
      return;
    }

    if (isResizing) {
      let left   = startEdges.left;
      let right  = startEdges.right;
      let top    = startEdges.top;
      let bottom = startEdges.bottom;

      // For bottom-edge resize, we only move the bottom
      if (resizeDir && resizeDir.indexOf('s') !== -1) {
        bottom = startEdges.bottom + dy;
      }

      // Compute new height with top anchored
      let height = bottom - top;

      // Enforce MIN_HEIGHT by adjusting only the bottom
      if (height < MIN_HEIGHT) {
        height = MIN_HEIGHT;
        bottom = top + height;
      }

      // Apply geometry (width stays the same on mobile bottom resize)
      const width = startEdges.right - startEdges.left;

      windowEl.style.left   = startEdges.left + 'px';
      windowEl.style.top    = top + 'px';
      windowEl.style.width  = width + 'px';
      windowEl.style.height = height + 'px';
      windowEl.style.right  = 'auto';
      windowEl.style.bottom = 'auto';
    }

    e.preventDefault();
  }, { passive: false });

  document.addEventListener('touchend', function () {
    if (!isMobileViewport()) return;
    if (supportsPointer) return;

    if (isWindowDragging || isResizing) {
      isWindowDragging = false;
      isResizing = false;
      resizeDir = '';
      startEdges = null;
      document.body.style.userSelect = '';
      windowEl.style.cursor = '';
      saveWindowState();
    }
  });

  // Bottom resize handle for mobile
  if (bottomResizeHandle) {
    bottomResizeHandle.addEventListener('touchstart', function (e) {
      if (!isMobileViewport()) return;
      if (isFullscreenWindowActive()) return;
      if (astIosKbActive) return;
      if (isIOSDevice() && (isKeyboardOpenIOS() || document.activeElement === inputEl)) return;

      if (!windowEl.classList.contains('astetica-chatbot-open')) return;

      if (!e.touches || e.touches.length === 0) return;

      const touch = e.touches[0];
      const rect = windowEl.getBoundingClientRect();

      isBottomResizingMobile = true;
      windowEl.classList.add('astetica-chatbot-resizing');
      bottomResizeStartY = touch.clientY;
      bottomResizeStartHeight = rect.height;

      e.preventDefault();
    }, { passive: false });
  }

  document.addEventListener('touchmove', function (e) {
    if (!isBottomResizingMobile) return;
    if (!isMobileViewport()) return;
    if (isFullscreenWindowActive()) {
      isBottomResizingMobile = false;
      windowEl.classList.remove('astetica-chatbot-resizing');
      return;
    }
    if (!e.touches || e.touches.length === 0) return;

    const touch = e.touches[0];
    const dy = touch.clientY - bottomResizeStartY;

    let newHeight = bottomResizeStartHeight + dy;

    // Clamp height between MIN_HEIGHT and 90% of viewport height
    const maxHeight = Math.round(window.innerHeight * 0.9);
    if (newHeight < MIN_HEIGHT) newHeight = MIN_HEIGHT;
    if (newHeight > maxHeight) newHeight = maxHeight;

    windowEl.style.height = newHeight + 'px';
    windowEl.style.bottom = 'auto';

    e.preventDefault();
  }, { passive: false });

  document.addEventListener('touchend', function () {
    if (!isBottomResizingMobile) return;

    if (!isMobileViewport()) return;

    isBottomResizingMobile = false;
    windowEl.classList.remove('astetica-chatbot-resizing');

    try {
      // Persist new size
      saveWindowState && saveWindowState();
    } catch (e) {
      // ignore
    }
  });

  // Drag functionality for launcher button
  let isDragging = false;
  let dragStartX = 0;
  let dragStartY = 0;
  let initialRootX = 0;
  let initialRootY = 0;
  let hasDragged = false;
  let activeTouchId = null;
  let suppressNextClick = false;
  const AST_LAUNCHER_POS_KEY = 'ast_widget_pos_v1';
  let launcherHasCustomPosition = false;

  function applyDefaultLauncherPosition() {
    root.style.bottom = '16px';
    root.style.right = '16px';
    root.style.top = 'auto';
    root.style.left = 'auto';
  }

  function loadLauncherPosition() {
    try {
      const saved = localStorage.getItem(AST_LAUNCHER_POS_KEY);
      if (saved) {
        const pos = JSON.parse(saved);
        if (pos && Number.isFinite(pos.x) && Number.isFinite(pos.y)) {
          const constrained = constrainToViewport(pos.x, pos.y);
          root.style.top = constrained.y + 'px';
          root.style.left = constrained.x + 'px';
          root.style.bottom = 'auto';
          root.style.right = 'auto';
          launcherHasCustomPosition = true;
          return;
        }
      }
    } catch (e) {
    }
    launcherHasCustomPosition = false;
    applyDefaultLauncherPosition();
  }

  function saveLauncherPosition() {
    try {
      const rect = root.getBoundingClientRect();
      const pos = {
        x: rect.left,
        y: rect.top
      };
      localStorage.setItem(AST_LAUNCHER_POS_KEY, JSON.stringify(pos));
      launcherHasCustomPosition = true;
    } catch (e) {
    }
  }

  function getRootPosition() {
    const rect = root.getBoundingClientRect();
    return {
      top: rect.top,
      left: rect.left
    };
  }

  function constrainToViewport(x, y) {
    const launcherSize = 72;
    const maxX = window.innerWidth - launcherSize;
    const maxY = window.innerHeight - launcherSize;
    
    x = Math.max(0, Math.min(x, maxX));
    y = Math.max(0, Math.min(y, maxY));
    
    return { x, y };
  }

  function updateRootPosition(x, y) {
    if (astShouldGuardFullscreenGeometry()) return;
    if (astIsIosFullscreenKbGuarded()) return;
    const constrained = constrainToViewport(x, y);
    root.style.left = constrained.x + 'px';
    root.style.top = constrained.y + 'px';
    root.style.bottom = 'auto';
    root.style.right = 'auto';
  }

  function getPointerCoords(e) {
    if (e.touches && e.touches.length > 0) {
      return {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY
      };
    }
    return {
      x: e.clientX,
      y: e.clientY
    };
  }

  function startDrag(e) {
    e.preventDefault();
    e.stopPropagation();
    resetLauncherDragState(false);
    
    isDragging = true;
    hasDragged = false;
    launcher.classList.add('dragging');
    if (e && e.touches && e.touches.length > 0) {
      activeTouchId = e.touches[0].identifier;
    } else {
      activeTouchId = null;
    }
    
    const coords = getPointerCoords(e);
    dragStartX = coords.x;
    dragStartY = coords.y;
    
    const pos = getRootPosition();
    initialRootX = pos.left;
    initialRootY = pos.top;
    
    launcher.style.transition = 'none';
  }

  function handleDragMove(e) {
    if (!isDragging) return;
    if (e && e.touches && typeof activeTouchId === 'number') {
      let hasActiveTouch = false;
      for (let i = 0; i < e.touches.length; i += 1) {
        if (e.touches[i].identifier === activeTouchId) {
          hasActiveTouch = true;
          break;
        }
      }
      if (!hasActiveTouch) return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    
    const coords = getPointerCoords(e);
    const deltaX = coords.x - dragStartX;
    const deltaY = coords.y - dragStartY;
    
    if ((Math.abs(deltaX) + Math.abs(deltaY)) > 6) {
      hasDragged = true;
      launcherLastDragAt = Date.now();
    }
    
    const newX = initialRootX + deltaX;
    const newY = initialRootY + deltaY;
    
    updateRootPosition(newX, newY);
  }

  function resetLauncherDragState(restoreTransition) {
    isDragging = false;
    activeTouchId = null;
    launcher.classList.remove('dragging');
    if (restoreTransition !== false) launcher.style.transition = '';
    hasDragged = false;
    document.removeEventListener('mousemove', handleDragMove);
    document.removeEventListener('mouseup', endDrag);
    document.removeEventListener('touchmove', handleDragMove);
    document.removeEventListener('touchend', endDrag);
    document.removeEventListener('touchcancel', endDrag);
  }

  function endDrag(e) {
    if (!isDragging) {
      resetLauncherDragState(true);
      return;
    }
    
    if (e && e.cancelable) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();
    
    if (hasDragged) {
      saveLauncherPosition();
      launcherLastDragAt = Date.now();
    }
    resetLauncherDragState(true);
  }

  launcher.addEventListener('mousedown', function(e) {
    startDrag(e);
    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', endDrag);
  });

  launcher.addEventListener('touchstart', function(e) {
    startDrag(e);
    document.addEventListener('touchmove', handleDragMove, { passive: false });
    document.addEventListener('touchend', endDrag);
    document.addEventListener('touchcancel', endDrag);
  });

  loadLauncherPosition();

  window.addEventListener('resize', function() {
    const nextW = window.innerWidth;
    const nextH = window.innerHeight;
    const widthChanged = Math.abs(nextW - lastViewportW) > 2;
    const heightChanged = Math.abs(nextH - lastViewportH) > 2;
    const likelyChromeResize = !widthChanged && heightChanged;
    const isMobile = isMobileViewport();

    updateIOSClass();
    astDbg('window.resize', { widthChanged: widthChanged, heightChanged: heightChanged, likelyChromeResize: likelyChromeResize });
    if (isIOSDevice() && windowEl.classList.contains('astetica-chatbot-open')) {
      if (isMobileFullscreenActive()) {
        astIosFullscreenKbActive = true;
        astIosFullscreenKbLastTick = Date.now();
        astMarkKbTransition(450);
        astMarkKbTransition(450);
        setTimeout(astUpdateKeyboardInsetVar, 0);
        setTimeout(astUpdateInputHeightVar, 0);
        astHeaderMovedDetector('window.resize-fullscreen');
        lastViewportW = nextW;
        lastViewportH = nextH;
        lastViewportVVH = null;
        return;
      } else {
        if (!likelyChromeResize || isKeyboardOpenIOS() || document.activeElement === inputEl) {
          scheduleIOSViewportSizing();
        }
        if (widthChanged) {
          applyIOSBaseSize();
        }
      }
    }
    if (!(isMobile && likelyChromeResize)) {
      if (!launcherHasCustomPosition) {
        applyDefaultLauncherPosition();
      } else {
      const pos = getRootPosition();
      const constrained = constrainToViewport(pos.left, pos.top);
      if (constrained.x !== pos.left || constrained.y !== pos.top) {
        updateRootPosition(constrained.x, constrained.y);
        saveLauncherPosition();
      }
      }
    }
    
    if (windowEl.classList.contains('astetica-chatbot-open')) {
      if (isMobileFullscreenActive()) {
        // Fullscreen is CSS-driven; keep geometry untouched.
      } else {
        const rect = windowEl.getBoundingClientRect();
        const currentWidth = rect.width;
        const currentHeight = rect.height;
        const currentLeft = rect.left;
        const currentTop = rect.top;
        
        const clamped = clampRectToViewport(currentLeft, currentTop, currentWidth, currentHeight);
        const isCenteredTransform = windowEl.style.transform && windowEl.style.transform.indexOf('translate(-50%') !== -1;
        if (!(isMobile && (likelyChromeResize || isCenteredTransform))) {
          if (clamped.left !== currentLeft || clamped.top !== currentTop) {
            windowEl.style.left = clamped.left + 'px';
            windowEl.style.top = clamped.top + 'px';
            saveWindowState();
          }
        }
        if (isMobile && widthChanged && windowEl.dataset.dragged !== '1') {
          const c = getVisualViewportCenterPx();
          mobileBaseCenterX = c.x;
          mobileBaseCenterY = c.y;
          applyMobileBaseCenterPosition();
        }
      }
    }

    lastViewportW = nextW;
    lastViewportH = nextH;
    lastViewportVVH = null;
  });

  window.addEventListener('orientationchange', function () {
    astDbg('window.orientationchange');
    if (isMobileFullscreenActive() && isIOSDevice()) {
      astIosFullscreenBaselineH = Math.round(window.innerHeight || 0);
      astMarkKbTransition(450);
      astIosFullscreenKbActive = true;
      astIosFullscreenKbLastTick = Date.now();
      astScheduleKeyboardInsetUpdateAdaptive();
      setTimeout(astUpdateInputHeightVar, 0);
      setTimeout(function () {
        astDbg('window.orientationchange:timeout0');
        astHeaderMovedDetector('orientationchange-timeout0');
      }, 0);
    }
  });

  if (inputEl) {
    function astIsInteractiveControlTarget(t) {
      return !!(t && t.closest && t.closest('#astetica-chatbot-send, #astetica-chatbot-new-chat, #astetica-chatbot-close, #astetica-chatbot-attach, button, a, [role="button"]'));
    }

    function astIsComposerTarget(t) {
      return !!(t && t.closest && t.closest('#astetica-chatbot-input, #astetica-chatbot-input-area, #astetica-chatbot-input-bar'));
    }

    function astFocusInputPreventScroll(e) {
      if (!(isMobileFullscreenActive && isMobileFullscreenActive()) || !isIOSDevice || !isIOSDevice()) return;
      if (!windowEl || !astIsIosFullscreenActive || !astIsIosFullscreenActive()) return;
      const t = e && e.target;
      if (!t || !t.closest) return;
      if (astIsInteractiveControlTarget(t)) return;
      if (!astIsComposerTarget(t)) return;
      const now = Date.now();
      astFocusInputPreventScroll._ts = astFocusInputPreventScroll._ts || 0;
      if (now - astFocusInputPreventScroll._ts < 400) return;
      astFocusInputPreventScroll._ts = now;

      if (e && e.cancelable && e.preventDefault) e.preventDefault();

      try { inputEl.focus({ preventScroll: true }); }
      catch (_) { try { inputEl.focus(); } catch (__) {} }

      if (typeof astMarkKbTransition === 'function') astMarkKbTransition(450);
      if (typeof astScheduleKeyboardInsetUpdateAdaptive === 'function') astScheduleKeyboardInsetUpdateAdaptive();
      try { astUpdateKeyboardInsetVar(); } catch (_) {}
      try { setTimeout(astUpdateInputHeightVar, 0); } catch (_) {}
      astBootstrapKbOpenPin('focus-prevent-scroll');
    }


    inputEl.addEventListener('touchstart', astFocusInputPreventScroll, { passive: false });
    inputEl.addEventListener('pointerdown', function (e) {
      if (e && e.pointerType === 'touch') return;
      astFocusInputPreventScroll(e);
    }, { passive: false });

    inputEl.addEventListener('focus', updateIOSKeyboardLayout, { passive: true });
    inputEl.addEventListener('blur', updateIOSKeyboardLayout, { passive: true });
    inputEl.addEventListener('focus', function () {
      astDbg('input focus', { fullscreen: isMobileFullscreenActive() });
      if (isMobileFullscreenActive() && isIOSDevice()) {
        astHeaderBaselineFocusTop = (document.getElementById('astetica-chatbot-header') || { getBoundingClientRect: function () { return { top: null }; } }).getBoundingClientRect().top;
        astDbg('input:focus:baseline', { baselineFocusTop: astHeaderBaselineFocusTop });
        // Mark keyboard transition and update inset -- the preventScroll
        // interceptor already prevents Safari's native focus-scroll so we
        // no longer need aggressive recenter/scrollTo corrections here.
        astMarkKbTransition(450);
        astIosFullscreenKbActive = true;
        astIosFullscreenKbLastTick = Date.now();
        setTimeout(astUpdateKeyboardInsetVar, 0);
        setTimeout(astUpdateInputHeightVar, 0);
        astBootstrapKbOpenPin('focus');
        astDbg('input:focus:done');
        astHeaderMovedDetector('input:focus');
      }
    }, { passive: true });
    inputEl.addEventListener('blur', function () {
      astDbg('input blur', { fullscreen: isMobileFullscreenActive() });
      if (isMobileFullscreenActive() && isIOSDevice()) {
        astStartForceCloseLock(700);
        forceKeyboardUnpinNow();
        astDbg('input:blur');
        astHeaderMovedDetector('input:blur');
      }
    }, { passive: true });
    if (AST_IOS_KEYBOARD_FIX) {
      inputEl.addEventListener('focus', astUpdateIosKeyboardPin, { passive: true });
      inputEl.addEventListener('blur', astUpdateIosKeyboardPin, { passive: true });
    }

    // ---------- iOS preventScroll focus fix ----------
    // Prevent iOS Safari's native focus-scroll (the cause of the "snap" on
    // keyboard open in fullscreen mode).  We intercept the tap on the input,
    // preventDefault() to stop Safari from scrolling/adjusting the viewport
    // geometry, and then manually call focus({ preventScroll: true }).
    // This only activates when the widget is in iOS mobile fullscreen mode;
    // on desktop / non-iOS the default browser behaviour is left untouched.
    function astPreventScrollFocus(e) {
      if (!isMobileFullscreenActive() || !isIOSDevice()) return;
      const t = e && e.target;
      if (!t || !t.closest) return;
      if (astIsInteractiveControlTarget(t)) return;
      if (!astIsComposerTarget(t)) return;
      // Only intercept if the input isn't already focused (avoid fighting
      // with caret repositioning taps inside an already-focused field).
      if (document.activeElement === inputEl) return;

      if (e.cancelable) e.preventDefault();
      astDbg('preventScroll-focus intercepted', { type: e.type });

      try {
        inputEl.focus({ preventScroll: true });
      } catch (err) {
        // Fallback for engines that don't support the options bag
        inputEl.focus();
      }

      // Kick keyboard-inset bookkeeping immediately
      astMarkKbTransition(450);
      astIosFullscreenKbActive = true;
      astIosFullscreenKbLastTick = Date.now();
      setTimeout(astUpdateKeyboardInsetVar, 0);
      setTimeout(astUpdateInputHeightVar, 0);
      astBootstrapKbOpenPin('tap-prevent-scroll');
    }

    inputEl.addEventListener('touchstart', astPreventScrollFocus, { passive: false });
    if (inputAreaEl) {
      inputAreaEl.addEventListener('touchstart', function () {
        astBootstrapKbOpenPin('composer-touch');
      }, { passive: true });
      inputAreaEl.addEventListener('pointerdown', function () {
        astBootstrapKbOpenPin('composer-pointer');
      }, { passive: true });
    }
  }

  function escapeHtml(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function linkify(text) {
    return text.replace(
      /(https?:\/\/[^\s<>"')]+[^\s<>"').,!?])/g,
      function (url) {
        return '<a href="' + url + '" target="_blank" rel="noopener noreferrer">' + url + '</a>';
      }
    );
  }

  function appendMessage(text, from, images) {
    const messageDiv = document.createElement('div');
    messageDiv.className = 'astetica-chatbot-message astetica-chatbot-' + (from === 'user' ? 'user' : 'assistant');
    if (images && images.length > 0) {
      messageDiv.classList.add('astetica-chatbot-message--has-image');
    }
    
    if (text) {
      const textWrap = document.createElement('span');
      textWrap.className = 'astetica-chatbot-message-text';
      textWrap.innerHTML = linkify(escapeHtml(text)).replace(/\n/g, '<br>');
      messageDiv.appendChild(textWrap);
    }
    
    if (images && images.length > 0) {
      images.forEach(dataUrl => {
        const img = document.createElement('img');
        img.src = dataUrl;
        img.className = 'astetica-chatbot-message-image';
        img.alt = 'User uploaded image';
        messageDiv.appendChild(img);
      });
    }
    
    messagesEl.appendChild(messageDiv);
    if (!astIsRestoringHistory) {
      pushHistory(from === 'user' ? 'user' : 'assistant', text);
    }
    setTimeout(() => {
      messagesEl.scrollTop = messagesEl.scrollHeight;
    }, 0);
  }

  function showTyping() {
    const typingDiv = document.getElementById('astetica-chatbot-typing-indicator');
    if (typingDiv) {
      typingDiv.remove();
    }
    
    const newTypingDiv = document.createElement('div');
    newTypingDiv.className = 'astetica-chatbot-message astetica-chatbot-typing';
    newTypingDiv.id = 'astetica-chatbot-typing-indicator';
    
    const dotsContainer = document.createElement('span');
    dotsContainer.className = 'astetica-chatbot-typing-dots';
    dotsContainer.innerHTML = '<span></span><span></span><span></span>';
    
    newTypingDiv.appendChild(dotsContainer);
    
    messagesEl.appendChild(newTypingDiv);
    setTimeout(() => {
      messagesEl.scrollTop = messagesEl.scrollHeight;
    }, 0);
  }

  function hideTyping() {
    const typingDiv = document.getElementById('astetica-chatbot-typing-indicator');
    if (typingDiv) {
      typingDiv.remove();
    }
  }

  function resetChat() {
    // Clear all messages
    if (messagesEl) {
      messagesEl.innerHTML = '';
      delete messagesEl.dataset.initialised;
    }

    // Remove typing indicator if present
    hideTyping && typeof hideTyping === 'function' && hideTyping();

    // Clear text input
    if (inputEl) {
      inputEl.value = '';
      resizeWidgetInput();
    }

    // Clear selected images and thumbnails
    widgetSelectedImages = [];
    if (typeof renderThumbnails === 'function') {
      renderThumbnails();
    }
    if (fileInput) {
      fileInput.value = '';
    }

    // No hardcoded greeting - let the model generate it naturally from the new system prompt
    if (messagesEl) {
      messagesEl.dataset.initialised = '1';
      // Model will generate greeting when user sends first message
    }
    clearChatHistory();
  }

  function openChat() {
    astDbg('openChat start', { fullscreen: isMobileFullscreenActive() });
    const isMobile = isMobileViewport();
    const headerTitleEl = document.getElementById('astetica-chatbot-header-title');
    const useMobileFullscreen = isMobile && AST_MOBILE_FULLSCREEN;

    if (!useMobileFullscreen) {
      astTraceWrite('html/root/window remove fullscreen classes (openChat non-fullscreen)');
      astDbg('class toggle', { target: 'html', action: 'remove', cls: 'ast-chat-fullscreen' });
      document.documentElement.classList.remove('ast-chat-fullscreen');
      astDbg('class toggle', { target: 'root', action: 'remove', cls: 'ast-fullscreen' });
      root.classList.remove('ast-fullscreen');
      astDbg('class toggle', { target: 'windowEl', action: 'remove', cls: 'astetica-chatbot-fullscreen' });
      windowEl.classList.remove('astetica-chatbot-fullscreen');
      unbindAstVisualViewport();
      clearMobileFullscreenStyles();
      unlockBodyScrollForFullscreen();
    }

    if (isMobile) {
      if (headerTitleEl) {
        headerTitleEl.textContent = 'Astetica Ai';
      }
      if (useMobileFullscreen) {
        astTraceWrite('html/root/window add fullscreen classes');
        astDbg('class toggle', { target: 'html', action: 'add', cls: 'ast-chat-fullscreen' });
        document.documentElement.classList.add('ast-chat-fullscreen');
        astDbg('class toggle', { target: 'root', action: 'add', cls: 'ast-fullscreen' });
        root.classList.add('ast-fullscreen');
        astDbg('class toggle', { target: 'windowEl', action: 'add', cls: 'astetica-chatbot-fullscreen' });
        windowEl.classList.add('astetica-chatbot-fullscreen');
        astDbg('fullscreen classes applied', {
          htmlClasses: document.documentElement.className,
          windowClasses: windowEl && windowEl.className
        });
        applyMobileFullscreenStyles();
        lockBodyScrollForFullscreen();
        astDbg('openChat after scroll lock', { fullscreen: isMobileFullscreenActive() });
        bindAstVisualViewport();
        if (isIOSDevice()) {
          astIosFullscreenBaselineH = Math.round(window.innerHeight || 0);
          astIosFullscreenKbActive = true;
          astIosFullscreenKbLastTick = Date.now();
          astMarkKbTransition(450);
          astScheduleKeyboardInsetUpdateAdaptive();
          setTimeout(astUpdateInputHeightVar, 0);
        }
        const headerOnOpen = document.getElementById('astetica-chatbot-header');
        astHeaderBaselineOpenTop = headerOnOpen ? headerOnOpen.getBoundingClientRect().top : null;
        astHeaderBaselineFocusTop = null;
        astDbg('openChat:fullscreen:post-lock', { baselineOpenTop: astHeaderBaselineOpenTop });
        astHeaderMovedDetector('openChat:post-lock');
      } else if (isIOSDevice()) {
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 360;
        const mobileWidth = Math.round(viewportWidth * 0.92);

        windowEl.style.position = 'fixed';
        windowEl.style.right = 'auto';
        windowEl.style.width = mobileWidth + 'px';
        applyIOSBaseSize();

        if (windowEl.dataset.dragged === '1') {
          restoreDraggedPosition();
        } else {
          const c = getVisualViewportCenterPx();
          mobileBaseCenterX = c.x;
          mobileBaseCenterY = c.y;
          applyMobileBaseCenterPosition();
        }

        scheduleIOSViewportSizing();
      } else {
        // Mobile: open as a large centered card (similar to embedded chat)
        const viewportHeight =
          window.innerHeight ||
          document.documentElement.clientHeight ||
          600;

        const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 360;

        // Width: around 92% of screen width
        const mobileWidth = Math.round(viewportWidth * 0.92);

        // Height: around 82% of viewport height
        const mobileHeight = Math.round(viewportHeight * 0.82);

        windowEl.style.position = 'fixed';
        windowEl.style.width = mobileWidth + 'px';
        astLogStyleWrite('windowEl', 'height', mobileHeight + 'px', 'before');
        windowEl.style.height = mobileHeight + 'px';
        astLogStyleWrite('windowEl', 'height', windowEl.style.height, 'after');
        windowEl.style.right = 'auto';
        windowEl.style.bottom = 'auto';
        if (windowEl.dataset.dragged === '1') {
          restoreDraggedPosition();
        } else {
          const c = getVisualViewportCenterPx();
          mobileBaseCenterX = c.x;
          mobileBaseCenterY = c.y;
          applyMobileBaseCenterPosition();
        }
      }
    } else {
      if (headerTitleEl) {
        headerTitleEl.textContent = 'Astetica Ai assistant';
      }
      // Desktop / larger screens: use saved state if available
      const savedState = loadWindowState();

      if (savedState) {
        windowEl.style.width = savedState.width + 'px';
        astLogStyleWrite('windowEl', 'height', savedState.height + 'px', 'before');
        windowEl.style.height = savedState.height + 'px';
        astLogStyleWrite('windowEl', 'height', windowEl.style.height, 'after');

        astLogStyleWrite('windowEl', 'left', savedState.left + 'px', 'before');
        windowEl.style.left = savedState.left + 'px';
        astLogStyleWrite('windowEl', 'left', windowEl.style.left, 'after');
        astLogStyleWrite('windowEl', 'top', savedState.top + 'px', 'before');
        windowEl.style.top = savedState.top + 'px';
        astLogStyleWrite('windowEl', 'top', windowEl.style.top, 'after');
        windowEl.style.right = 'auto';
        windowEl.style.bottom = 'auto';
      } else {
        // Default bottom-right placement
        const defaultWidth = 400;
        const defaultHeight = 550;

        const left = window.innerWidth - defaultWidth - 24;
        const top = window.innerHeight - defaultHeight - 100;

        windowEl.style.width = defaultWidth + 'px';
        astLogStyleWrite('windowEl', 'height', defaultHeight + 'px', 'before');
        windowEl.style.height = defaultHeight + 'px';
        astLogStyleWrite('windowEl', 'height', windowEl.style.height, 'after');

        astLogStyleWrite('windowEl', 'left', left + 'px', 'before');
        windowEl.style.left = left + 'px';
        astLogStyleWrite('windowEl', 'left', windowEl.style.left, 'after');
        astLogStyleWrite('windowEl', 'top', top + 'px', 'before');
        windowEl.style.top = top + 'px';
        astLogStyleWrite('windowEl', 'top', windowEl.style.top, 'after');
        windowEl.style.right = 'auto';
        windowEl.style.bottom = 'auto';
      }

      windowEl.style.position = 'fixed';
      astLogStyleWrite('windowEl', 'transform', '', 'before');
      windowEl.style.transform = '';
      astLogStyleWrite('windowEl', 'transform', windowEl.style.transform, 'after');
    }

    windowEl.classList.add('astetica-chatbot-open');
    root.classList.add('astetica-chatbot-opened');
    updateIOSClass();

    if (!messagesEl.dataset.initialised) {
      messagesEl.dataset.initialised = '1';
      // No hardcoded greeting - model will generate it naturally when user sends first message
    }

    if (isMobileFullscreenActive()) {
      messagesEl.scrollTop = messagesEl.scrollHeight;
    } else {
      astDbg('focus-call:openChat');
      inputEl.focus();
    }
    astDbg('openChat end', { fullscreen: isMobileFullscreenActive() });
    if (AST_IOS_KEYBOARD_FIX && isIOSDevice() && !isMobileFullscreenActive()) {
      astUpdateIosKeyboardPin();
    }
  }

  function closeChat() {
    const wasFullscreen = isFullscreenWindowActive();
    astDbg('closeChat start', { wasFullscreen: wasFullscreen });
    astTraceWrite('html/root/window remove fullscreen classes (closeChat)');
    astDbg('class toggle', { target: 'html', action: 'remove', cls: 'ast-chat-fullscreen' });
    document.documentElement.classList.remove('ast-chat-fullscreen');
    astDbg('class toggle', { target: 'root', action: 'remove', cls: 'ast-fullscreen' });
    root.classList.remove('ast-fullscreen');
    astDbg('class toggle', { target: 'windowEl', action: 'remove', cls: 'astetica-chatbot-fullscreen' });
    windowEl.classList.remove('astetica-chatbot-fullscreen');
    unbindAstVisualViewport();
    windowEl.classList.remove('astetica-chatbot-open');
    root.classList.remove('astetica-chatbot-opened');
    if (wasFullscreen) {
      clearMobileFullscreenStyles();
      astLogStyleWrite('windowEl', 'top', '', 'before');
      windowEl.style.top = '';
      astLogStyleWrite('windowEl', 'top', windowEl.style.top, 'after');
      astLogStyleWrite('windowEl', 'left', '', 'before');
      windowEl.style.left = '';
      astLogStyleWrite('windowEl', 'left', windowEl.style.left, 'after');
      windowEl.style.right = '';
      windowEl.style.bottom = '';
      windowEl.style.width = '';
      astLogStyleWrite('windowEl', 'height', '', 'before');
      windowEl.style.height = '';
      astLogStyleWrite('windowEl', 'height', windowEl.style.height, 'after');
      windowEl.style.maxHeight = '';
      astLogStyleWrite('windowEl', 'transform', '', 'before');
      windowEl.style.transform = '';
      astLogStyleWrite('windowEl', 'transform', windowEl.style.transform, 'after');
    }
    windowEl.style.removeProperty('--ast-kb-inset');
    unlockBodyScrollForFullscreen();
    astIosFullscreenKbActive = false;
    astIosFullscreenKbLastTick = 0;
    astHeaderBaselineOpenTop = null;
    astHeaderBaselineFocusTop = null;
    astDbg('closeChat end', {});
  }

  function astBindPrimaryTap(el, fn) {
    if (!el || typeof fn !== 'function') return;
    function invoke(e) {
      const now = Date.now();
      if (el.__astTapHandledUntil && now < el.__astTapHandledUntil) return;
      el.__astTapHandledUntil = now + 450;
      if (e && e.cancelable) e.preventDefault();
      fn(e);
    }
    el.addEventListener('touchend', invoke, { passive: false });
    el.addEventListener('pointerup', function (e) {
      if (!e || e.pointerType === 'touch') invoke(e);
    }, { passive: false });
  }

  closeBtn.addEventListener('click', function () {
    if (closeBtn.__astTapHandledUntil && Date.now() < closeBtn.__astTapHandledUntil) return;
    closeChat();
  });
  astBindPrimaryTap(closeBtn, function () {
    closeChat();
  });

  if (newChatBtn) {
    newChatBtn.addEventListener('click', function (e) {
      if (newChatBtn.__astTapHandledUntil && Date.now() < newChatBtn.__astTapHandledUntil) return;
      e.preventDefault();
      e.stopPropagation();
      resetChat();
    });
    astBindPrimaryTap(newChatBtn, function () {
      resetChat();
    });
  }

  function readFilesAsDataUrls(fileList) {
    return Promise.all(
      Array.from(fileList).map(file => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      })
    );
  }

  function renderThumbnails() {
    if (!thumbsContainer) return;
    thumbsContainer.innerHTML = '';
    widgetSelectedImages.forEach((dataUrl, index) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'astetica-chatbot-thumb-wrapper';
      
      const thumb = document.createElement('img');
      thumb.src = dataUrl;
      thumb.className = 'astetica-chatbot-thumb';
      thumb.alt = 'Selected image';
      
      const removeBtn = document.createElement('button');
      removeBtn.className = 'astetica-x-btn astetica-chatbot-thumb-remove';
      removeBtn.setAttribute('aria-label', 'Remove image');
      removeBtn.type = 'button';
      removeBtn.innerHTML = `
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <line x1="5" y1="5" x2="19" y2="19" />
          <line x1="5" y1="19" x2="19" y2="5" />
        </svg>
      `;
      removeBtn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        widgetSelectedImages.splice(index, 1);
        renderThumbnails();
      });
      
      wrapper.appendChild(thumb);
      wrapper.appendChild(removeBtn);
      thumbsContainer.appendChild(wrapper);
    });
  }

  if (attachBtn && fileInput && thumbsContainer) {
    attachBtn.addEventListener('click', function () {
      if (attachBtn.__astTapHandledUntil && Date.now() < attachBtn.__astTapHandledUntil) return;
      fileInput.click();
    });
    astBindPrimaryTap(attachBtn, function () {
      fileInput.click();
    });

    fileInput.addEventListener('change', async function (event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      try {
        const dataUrls = await readFilesAsDataUrls(files);
        widgetSelectedImages = widgetSelectedImages.concat(dataUrls);
        renderThumbnails();
      } catch (e) {
        console.error('Error reading files:', e);
        if (isLocalhost()) {
          alert('Error reading image files. Please try again.');
        }
      }
      
      fileInput.value = '';
    });
  }

  function handleError(e) {
    hideTyping();
    appendMessage(
      'The Astetica assistant is not available right now. Please try again later.',
      'assistant'
    );
    if (isLocalhost()) {
      console.error('Astetica chatbot error:', e);
    }
  }

  let isSending = false;
  let isComposing = false;
  const WIDGET_MAX_ROWS = 6;

  if (inputEl && inputEl.tagName !== 'TEXTAREA') {
    console.warn('Astetica chatbot widget: input is not a textarea; multiline autosize disabled.');
  }

  function getWidgetLineHeight(el) {
    const style = window.getComputedStyle(el);
    const lineHeight = parseFloat(style.lineHeight);
    if (!Number.isNaN(lineHeight)) return lineHeight;
    const fontSize = parseFloat(style.fontSize) || 16;
    return fontSize * 1.5;
  }

  function resizeWidgetInput() {
    if (astShouldGuardFullscreenGeometry()) return;
    if (!inputEl || inputEl.tagName !== 'TEXTAREA') return;
    const lineHeight = getWidgetLineHeight(inputEl);
    const maxHeight = Math.round(lineHeight * WIDGET_MAX_ROWS);

    inputEl.style.height = 'auto';
    const nextHeight = Math.min(inputEl.scrollHeight, maxHeight);
    inputEl.style.height = nextHeight + 'px';
    inputEl.style.overflowY = inputEl.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }

  async function sendMessage() {
    if (isSending) return;

    const text = inputEl.value.trim();
    
    if (!text && widgetSelectedImages.length === 0) return;

    if (isMobileFullscreenActive() && isIOSDevice()) {
      try { inputEl.blur(); } catch (e) {}
      astStartForceCloseLock(900);
      forceKeyboardUnpinNow();
    }

    isSending = true;
    inputEl.disabled = true;
    sendBtn.disabled = true;
    if (attachBtn) attachBtn.disabled = true;
    
    const imagesToSend = [...widgetSelectedImages];
    
    appendMessage(text, 'user', imagesToSend);
    
    inputEl.value = '';
    resizeWidgetInput();
    widgetSelectedImages = [];
    renderThumbnails();
    if (fileInput) fileInput.value = '';
    
    showTyping();

    try {
      const requestBody = {
        message: text || '',
        source: "floating_widget",
        session_id: getAstSessionId(),
        images: imagesToSend.length > 0 ? imagesToSend : undefined,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null
      };

      if (!requestBody.images) {
        delete requestBody.images;
      }

      const anonId = getAnonId();
      let identityToken = await getIdentityToken();
      let res = await postChatRequest(requestBody, anonId, identityToken);
      if (res.status === 401 || res.status === 403) {
        identityToken = await getIdentityToken({ forceRefresh: true });
        if (identityToken) {
          res = await postChatRequest(requestBody, anonId, identityToken);
        }
      }

      if (!res.ok) {
        throw new Error('Non-OK response: ' + res.status);
      }

      const data = await res.json();
      hideTyping();
      
      if (data.success === true && data.reply) {
        appendMessage(data.reply, 'assistant');
      } else {
        const reply = data.reply || data.answer || data.message || 'Got it.';
        appendMessage(reply, 'assistant');
      }
    } catch (e) {
      handleError(e);
    } finally {
      isSending = false;
      inputEl.disabled = false;
      sendBtn.disabled = false;
      if (attachBtn) attachBtn.disabled = false;
      setTimeout(() => {
        if (!isMobileFullscreenActive()) {
          astDbg('focus-call:sendMessage');
          inputEl.focus();
        }
      }, 100);
    }
  }

  sendBtn.addEventListener('click', function () {
    if (sendBtn.__astTapHandledUntil && Date.now() < sendBtn.__astTapHandledUntil) return;
    sendMessage();
  });
  astBindPrimaryTap(sendBtn, function () {
    sendMessage();
  });

  if (inputEl && !inputEl.__astGrowBound) {
    inputEl.__astGrowBound = true;

    inputEl.addEventListener('compositionstart', function () {
      isComposing = true;
    });

    inputEl.addEventListener('compositionend', function () {
      isComposing = false;
    });

    inputEl.addEventListener('input', function () {
      resizeWidgetInput();
    });

    inputEl.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey && !e.isComposing && !isComposing) {
        e.preventDefault();
        sendMessage();
      }
    });

    resizeWidgetInput();
  }

  const widgetHistory = loadChatHistory();
  if (widgetHistory.length > 0 && !messagesEl.dataset.astHistoryRestored) {
    if (!messagesEl.firstChild) {
      astIsRestoringHistory = true;
      widgetHistory.forEach(function (item) {
        const from = (item.role === 'user') ? 'user' : 'assistant';
        appendMessage(item.text, from);
      });
      astIsRestoringHistory = false;
    }
    messagesEl.dataset.astHistoryRestored = '1';
  }

  (function setupAstDebug() {
    if (!location || !location.search || !location.search.includes('ast_debug=1')) return;

    const logEvent = (e) => {
      const path = e.composedPath ? e.composedPath().slice(0, 4) : [];
      const labels = path.map((el) => {
        if (!el || !el.tagName) return String(el);
        const id = el.id ? `#${el.id}` : '';
        const cls = el.className && typeof el.className === 'string'
          ? `.${el.className.trim().replace(/\s+/g, '.')}`
          : '';
        return `${el.tagName.toLowerCase()}${id}${cls}`;
      });
      console.log('[ast_debug][widget]', e.type, 'target=', e.target, 'defaultPrevented=', e.defaultPrevented, 'path=', labels);
    };

    ['pointerdown', 'mousedown', 'mousemove', 'selectstart'].forEach((evt) => {
      document.addEventListener(evt, logEvent, true);
    });
  })();
  setTimeout(function () {
    try {
      var existing = document.getElementById('astetica-chatbot-launcher');
      if (!existing && typeof createLauncher === 'function') createLauncher();
      if (!existing && typeof astCreateLauncher === 'function') astCreateLauncher();
    } catch (e) {}
  }, 0);

  } catch (e) {
    console.error('AST Widget init error', e);
  }
})();
